// ============================================
// EXPEDIENTES.JS — Lista de expedientes (CRM) + modal de captura
// FASE 2B PARTE B.2: lista profesional con jerarquia visible.
//   - 5 pestanas (Activos / Ganados / Perdidos / Descartados / Garantias)
//   - Buscador + filtros profesionales (chips estado funnel, responsable,
//     grupo cliente, tipo necesidad) — replicando ClientesNuevo.js
//   - Cards con badges de tipo_expediente y dias sin movimiento
//   - localStorage 'beep_expedientes_filtros' con reset por cambio de token
//
// La vista de detalle individual se construye en B.3. Por ahora el click
// solo registra en consola (placeholder preservado del codigo previo).
//
// Backend: el modulo se llama "oportunidades" en la BD pero la UI siempre
// usa "Expediente" porque es la que el equipo usa.
// ============================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GarantiasAvisosPanel from './components/GarantiasAvisosPanel';
import { createPortal } from 'react-dom';
import { API_BASE_CLI } from './components/helpersClientes';
import SelectorClienteExpediente from './components/SelectorClienteExpediente';
import FichaExpediente from './components/expedientes/FichaExpediente';
// TRANSPARENCIA-TOTAL: reusamos el Kanban y las tarjetas-resumen del panel global
// (ya no se monta como pantalla aparte; su contenido se fusiono aqui).
// Las cuatro tarjetas-resumen (StatCard) se retiraron en EXPEDIENTES-RESUMEN (2026-09-17): una de
// ellas enseñaba la facturacion de toda la empresa a cualquiera. Ya solo se reusa el Kanban.
import { VistaKanban } from './ControlExpedientes';
import ModalAvisarResponsable from './components/expedientes/ModalAvisarResponsable';
import ActividadExpedientes from './components/expedientes/ActividadExpedientes';
import CalendarioCampo from './CalendarioCampo';
import Contratistas from './Contratistas';
// DOS-TIEMPOS: wizard de contexto por rondas (Tiempo 2, opcional y adaptativo).
import { storage, STORAGE_KEYS } from './utils/storage';
import { useAutoRefresh } from './hooks/useAutoRefresh';
import { useAnchoViewport } from './hooks/useResponsive';
import { textoUbicacionLibre } from './utils/ubicacionExpediente';
// Estilo/format compartido de las vistas de expedientes (semaforo, tipo, moneda).
import { SEMAFORO, TIPO_VISUAL, textoDias, MarcaSinAutorizar, lecturaMonto, diasDesde } from './utils/estiloExpedientes';
// EXP-UX: iconos Lucide (consistentes en todo dispositivo, sin emojis)
import {
  Building2, MapPin, Calendar, Folder, Paperclip, ChevronRight,
  LayoutGrid, Table2, CheckCircle2, Clock, ArrowUpDown,
  BarChart3, SlidersHorizontal, Bookmark,
} from 'lucide-react';
// EXPEDIENTES-RESUMEN (2026-09-17)
// FRENTE A: la tabla ancha vive dentro de una ventana ACOTADA, para que su barra horizontal quede
//   siempre a la vista. Antes crecia con las 168 filas y la barra caia 12,691 px fuera de pantalla.
import ScrollportTabla from './components/ScrollportTabla';
// FRENTE B/C: la tercera vista, solo para direccion (el endpoint que la alimenta da 403 al resto).
import ResumenEmpresa from './components/expedientes/ResumenEmpresa';
// FRENTE D: los filtros, plegados detras de un boton; y las vistas guardadas.
import PanelFiltrosExpedientes from './components/expedientes/PanelFiltrosExpedientes';
import { leerVistas, guardarVista as guardarVistaFiltros, borrarVista as borrarVistaFiltros,
         MAX_VISTAS_VISIBLES, vistaActiva } from './utils/vistasGuardadas';
// FLUJOS-FIJOS (Entrega 1): tipo de trabajo al crear + gating del badge de paso.
import { TIPOS_TRABAJO } from './utils/flujos';
// El badge de paso solo lo mueve a mano direccion (regla de flujo). El resto de la
// vista es abierta a todos; useUsuario se usa unicamente para ese candado.
import { useUsuario } from './context/UsuarioContext';
import SelectorFamilia from './components/SelectorFamilia';
import useFamilias from './hooks/useFamilias';

const API = API_BASE_CLI;

const COLOR_BEEP_AZUL = '#185FA5';
const COLOR_BEEP_LIMA = '#94b803';
const COLOR_OSCURO    = '#270f2b';
const COLOR_FONDO     = '#f3f4f4';
const COLOR_BORDE     = '#e5e7eb';
const COLOR_GRIS      = '#5e666e';
const COLOR_GRIS_CL   = '#9aa0a6';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Pestanas. La pestana 'garantias' usa tipo=todos en el GET y luego se
// filtra en frontend por tipo_expediente === 'garantia'.
// EXP-ARCHIVO (2026-09-10): las tres pestanas de cierre (Ganados / Perdidos / Descartados) se
// fusionaron en UNA sola, "Archivo". El resultado del cierre paso a ser un FILTRO dentro de ella.
// Garantias NO entra al Archivo: es postventa, no un cierre.
const PESTANAS = [
  { id: 'activos',     label: 'Activos',     queryTipo: 'activos' },
  { id: 'archivo',     label: 'Archivo',     queryTipo: 'archivo' },
  { id: 'garantias',   label: 'Garantias',   queryTipo: 'todos' },
  // ACTIVIDAD-GLOBAL (2026-07-11): timeline de quien-hizo-que sobre TODOS los
  // expedientes (fuente: mismo historial firmado). No lista expedientes.
  { id: 'actividad',   label: 'Actividad',   queryTipo: null },
];

// Visual unico para los 19 estados del funnel + cierres legacy.
// Compatible con lo que devuelve el backend en `estado` (texto).
const ESTADO_VISUAL_OPORTUNIDAD = {
  nuevo_interes:        { color: '#93C5FD', label: 'Contexto del proyecto', descripcion: 'Datos del interes del cliente' },
  solicitud_cliente:    { color: '#3B82F6', label: 'Solicitud Cliente',    descripcion: 'Cliente existente solicito servicio' },
  contactado:           { color: '#2563EB', label: 'Contactado',           descripcion: 'Primer contacto realizado' },
  visita_programada:    { color: '#1D4ED8', label: 'Visita Programada',    descripcion: 'Visita agendada con cliente' },
  visita_realizada:     { color: '#10B981', label: 'Visita Realizada',     descripcion: 'Visita tecnica completada' },
  en_cotizacion:        { color: '#F97316', label: 'En Cotizacion',        descripcion: 'Cotizacion en preparacion' },
  cotizacion_aprobada:  { color: '#22C55E', label: 'Cotizacion Aprobada',  descripcion: 'Cliente aprobo la cotizacion' },
  esperando_anticipo:   { color: '#FBBF24', label: 'Esperando Anticipo',   descripcion: 'Esperando pago anticipado del cliente' },
  anticipo_recibido:    { color: '#16A34A', label: 'Anticipo Recibido',    descripcion: 'Anticipo confirmado en banco' },
  en_compras:           { color: '#A855F7', label: 'En Compras',           descripcion: 'Solicitando material faltante' },
  almacen_revision:     { color: '#C084FC', label: 'Almacen Revision',     descripcion: 'Almacen revisando disponibilidad' },
  material_completo:    { color: '#15803D', label: 'Material Completo',    descripcion: 'Listo para programar' },
  en_programacion:      { color: '#0EA5E9', label: 'En Programacion',      descripcion: 'Generando ruta y agenda' },
  programado:           { color: '#0284C7', label: 'Programado',           descripcion: 'Fecha y tecnico asignados' },
  en_proceso:           { color: '#EA580C', label: 'En Proceso',           descripcion: 'Instalacion o servicio en curso' },
  pendiente_finiquito:  { color: '#FBBF24', label: 'Pendiente Finiquito',  descripcion: 'Esperando pago final' },
  pagado_completo:      { color: '#15803D', label: 'Pagado Completo',      descripcion: 'Pago total recibido' },
  en_garantia:          { color: '#10B981', label: 'En Garantia',          descripcion: 'Periodo de garantia activo' },
  archivado:            { color: '#6B7280', label: 'Archivado',            descripcion: 'Expediente cerrado' },
  // Cierres legacy (perdido / cancelado siguen vivos para /cerrar-perdido y /descartar)
  perdido:              { color: '#dc2626', label: 'Perdido',              descripcion: 'No se vendio' },
  cancelado:            { color: '#9ca3af', label: 'Descartado',           descripcion: 'Duplicado o error' },
  // FLUJOS-FIJOS (Entrega 1): pasos de los 3 flujos (espejo de mig_066).
  // Mantener sincronizado con helpersExpedientes.js.
  visita:               { color: '#1D4ED8', label: 'Levantamiento',        descripcion: 'Levantamiento tecnico del sitio (opcional)' },
  solicitud:            { color: '#3B82F6', label: 'Solicitud',            descripcion: 'Que pide el cliente' },
  diagnostico:          { color: '#0EA5E9', label: 'Diagnostico',          descripcion: 'Diagnostico del equipo' },
  cotizacion:           { color: '#F97316', label: 'Cotizacion',           descripcion: 'Cotizacion autorizada vinculada' },
  anticipo:             { color: '#FBBF24', label: 'Anticipo',             descripcion: 'Pago registrado' },
  preparacion:          { color: '#A855F7', label: 'Preparacion',          descripcion: 'Checklist de material' },
  compra:               { color: '#C084FC', label: 'Compra',               descripcion: 'Faltantes en compra' },
  almacen:              { color: '#15803D', label: 'Almacen',              descripcion: 'Salida de material' },
  instalacion:          { color: '#EA580C', label: 'Instalacion',          descripcion: 'Trabajo en sitio' },
  reparacion:           { color: '#DC2626', label: 'Ejecucion del servicio', descripcion: 'Servicio tecnico realizado' },
  entrega:              { color: '#10B981', label: 'Entrega',              descripcion: 'Conformidad del cliente' },
  facturacion:          { color: '#6366F1', label: 'Facturacion',          descripcion: 'Factura emitida' },
  cerrado:              { color: '#16A34A', label: 'Cerrado',              descripcion: 'Flujo completado (ganado)' },
};

// Codigos validos para el selector de filtro "Estado:" (solo pestana Activos)
// y para el override manual de direccion. FLUJOS-FIJOS: son los PASOS de los
// 3 flujos (union); los estados del funnel viejo de 19 ya se migraron.
// Excluye cerrado/perdido/cancelado (viven en sus pestanas de cierre).
const ESTADOS_VALIDOS_FUNNEL = [
  'nuevo_interes', 'visita', 'solicitud', 'diagnostico', 'cotizacion',
  'anticipo', 'preparacion', 'compra', 'almacen', 'instalacion',
  'reparacion', 'entrega', 'facturacion',
];

// Visual para tipo_expediente !== 'normal'
const TIPO_EXPEDIENTE_VISUAL = {
  anexo:              { color: '#ea580c', label: 'Anexo',     icono: '↳', descripcion: 'Trabajo extra durante una obra activa' },
  garantia:           { color: '#dc2626', label: 'Garantia',  icono: '⚠', descripcion: 'Reclamo bajo garantia de un servicio cerrado' },
  servicio_postventa: { color: '#185FA5', label: 'Postventa', icono: '↻', descripcion: 'Nueva venta a un cliente con expediente archivado' },
};

// Visual para badges de cierre (tipo_cierre)
const CIERRE_VISUAL = {
  ganado:     { color: '#1D9E75', label: 'Ganado' },
  perdido:    { color: '#dc2626', label: 'Perdido' },
  descartado: { color: '#9ca3af', label: 'Descartado' },
};

const STORAGE_KEY_FILTROS       = 'beep_expedientes_filtros';
const STORAGE_KEY_TOKEN_PREVIO  = 'beep_expedientes_token_previo';

// EXP-ARCHIVO: chips del filtro "Resultado" dentro de Archivo. El orden es el de la barra.
const RESULTADOS_ARCHIVO = [
  { id: '',           label: 'Todos' },
  { id: 'ganado',     label: 'Ganados' },
  { id: 'perdido',    label: 'Perdidos' },
  { id: 'descartado', label: 'Descartados' },
];
// Pestanas viejas -> Archivo + filtro de Resultado equivalente (preferencias ya guardadas y
// enlaces antiguos). Se mantiene a perpetuidad: es barato y evita que nadie pierda su vista.
// Map (no objeto plano): con un objeto, una clave heredada del prototipo guardada a mano en
// localStorage (p.ej. "constructor") daria un valor truthy y se colaria como filtro.
const PESTANA_LEGADA_A_RESULTADO = new Map([['ganados', 'ganado'], ['perdidos', 'perdido'], ['descartados', 'descartado']]);
function migrarPestanaGuardada(guardados) {
  const p = guardados?.pestana || 'activos';
  const rGuardado = guardados?.resultadoFiltro || '';
  // `migro`: venia de una pestana vieja, asi que su orden guardado (Fecha) ya no es el adecuado
  // para Archivo; el llamador lo cambia a "Cerrado el" descendente.
  if (PESTANA_LEGADA_A_RESULTADO.has(p)) return { pestana: 'archivo', resultado: PESTANA_LEGADA_A_RESULTADO.get(p), migro: true };
  const valido = RESULTADOS_ARCHIVO.some(r => r.id === rGuardado);
  return { pestana: p, resultado: valido ? rGuardado : '', migro: false };
}

// Formato valido para el filtro de fechas (input type="date" -> YYYY-MM-DD).
// Sanitiza lo hidratado de localStorage: cualquier basura se descarta.
const FORMATO_FECHA_FILTRO = /^\d{4}-\d{2}-\d{2}$/;

// Etiqueta corta para el chip de fecha. Parsea las partes a mano para evitar
// el corrimiento de dia de new Date('YYYY-MM-DD') (UTC vs zona local).
function etiquetaFechaFiltro(ymd) {
  if (!ymd || !FORMATO_FECHA_FILTRO.test(ymd)) return ymd || '';
  const [a, m, d] = ymd.split('-').map(Number);
  return new Date(a, m - 1, d).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
}

function haceCuanto(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  const seg = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seg < 60)        return 'hace unos segundos';
  if (seg < 3600)      return `hace ${Math.floor(seg / 60)} min`;
  if (seg < 86400)     return `hace ${Math.floor(seg / 3600)} h`;
  if (seg < 604800)    return `hace ${Math.floor(seg / 86400)} dia${seg < 172800 ? '' : 's'}`;
  if (seg < 2592000)   return `hace ${Math.floor(seg / 604800)} sem`;
  return `hace ${Math.floor(seg / 2592000)} mes${seg < 5184000 ? '' : 'es'}`;
}

function leerFiltrosGuardados() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FILTROS);
    if (!raw) return null;
    const o = JSON.parse(raw);
    return (o && typeof o === 'object') ? o : null;
  } catch { return null; }
}

// TRANSPARENCIA-TOTAL: semaforo por dias en el paso actual. La fuente de verdad
// de los umbrales es el BACKEND (panel.umbrales de GET /api/control-expedientes);
// los valores 3/7 son solo respaldo mientras el panel no ha cargado. Asi el filtro
// "Avance" y el chip de la tabla nunca se desincronizan del resumen/Kanban.
const UMBRAL_AMARILLO_FALLBACK = 3;
const UMBRAL_ROJO_FALLBACK = 7;
function semaforoDe(dias, umbrales) {
  const amarillo = Number(umbrales?.amarillo) || UMBRAL_AMARILLO_FALLBACK;
  const rojo = Number(umbrales?.rojo) || UMBRAL_ROJO_FALLBACK;
  const d = Number(dias) || 0;
  if (d > rojo) return 'rojo';
  if (d >= amarillo) return 'amarillo';
  return 'verde';
}

// Filtro "Tipo de trabajo" — los 3 flujos fijos (workflow_codigo del expediente).
const TIPOS_TRABAJO_FILTRO = [
  { id: 'instalacion',  label: 'Instalacion' },
  { id: 'reparaciones', label: 'Servicios' },
  { id: 'ventas',       label: 'Ventas' },
];

// EXPEDIENTES-RESUMEN (2026-09-17): filtro por ESTADO DE LA COTIZACION de la que sale el monto.
// El backend lo manda en `monto_estado` (la MISMA cotizacion que resuelve la columna Monto). Es el
// filtro al que llevan los renglones de dinero del Resumen: "de estos $9.1M en borrador, ¿cuales
// son?" deja de ser una pregunta sin respuesta.
const COTIZACION_FILTRO = [
  { id: 'autorizada', label: 'Autorizadas',  color: '#16a34a' },
  { id: 'enviada',    label: 'Enviadas',     color: '#2f5f9e' },
  { id: 'borrador',   label: 'En borrador',  color: '#9aa0a6' },
  { id: 'rechazada',  label: 'Rechazadas',   color: '#dc2626' },
];

// Las TRES vistas de la pantalla. El Resumen solo se dibuja para quien puede verlo (FRENTE C).
const VISTAS = [
  { id: 'resumen', label: 'Resumen', Icono: BarChart3, soloDireccion: true },
  { id: 'tabla',   label: 'Tabla',   Icono: Table2 },
  { id: 'kanban',  label: 'Kanban',  Icono: LayoutGrid },
];

// Filtro por semaforo (estado de avance por dias en el paso).
const SEMAFORO_FILTRO = [
  { id: 'rojo',     label: 'Atorados', color: SEMAFORO.rojo.color },
  { id: 'amarillo', label: 'Tardando', color: SEMAFORO.amarillo.color },
  { id: 'verde',    label: 'Al dia',   color: SEMAFORO.verde.color },
];

// Columnas ordenables de la tabla (se ordena por PADRES, los anexos quedan
// EXP-ARCHIVO: fecha en que se cerro el expediente. El backend guarda `fecha_cierre` y
// `cerrado_at` en el mismo UPDATE del cierre; se toma la primera que exista y, de ultimo
// recurso, la de actualizacion (expedientes cerrados antes de que existieran esos campos).
function fechaCierreMs(exp) {
  const v = exp.fecha_cierre || exp.cerrado_at || exp.actualizado_at;
  const t = v ? new Date(v).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
}

// anidados bajo su padre). Default: fecha del expediente, mas nuevo primero.
function valorOrdenExpediente(exp, campo) {
  switch (campo) {
    case 'titulo':      return (exp.titulo || '').toLowerCase();
    case 'folio':       return (exp.folio || '').toLowerCase();
    case 'tipo':        return exp.workflow_codigo || '';
    case 'paso':        return exp.paso_orden ?? 999;
    case 'responsable': return (exp.responsable_nombre || '').toLowerCase();
    case 'creador':     return (exp.creado_por_nombre || '').toLowerCase();
    case 'dias':        return Number(exp.dias_en_paso) || 0;
    // EXP-ARCHIVO: columnas propias de la pestana Archivo.
    case 'resultado':   return { ganado: 1, perdido: 2, descartado: 3 }[exp.tipo_cierre] || 9;
    case 'cerrado':     return fechaCierreMs(exp);
    case 'monto':       return exp.monto == null ? -1 : Number(exp.monto);
    case 'fecha':       return exp.fecha_interes ? new Date(exp.fecha_interes).getTime() : 0;
    default:            return 0;
  }
}

export default function Expedientes({ esCelular, modoSeleccion = false, seleccionFolio = '', etiquetaSeleccion = '', onElegir, onCancelarSeleccion, aperturaInicialId = null, onAperturaConsumida } = {}) {
  // EXP-UX (veto Don Benito): la tabla de 8 columnas se corta en PC VERTICAL
  // (1080px) porque esCelular solo cambia a tarjetas por debajo de 768. Usamos
  // useAnchoViewport con el umbral del proyecto (<1100 = compacto) para que PC
  // vertical, tablet y anchos intermedios caigan en la vista tarjeta (que ya
  // resuelve bien el ancho y trae el boton "Ver expediente" a 44px).
  const anchoVp = useAnchoViewport();
  const compacto = esCelular || anchoVp < 1100;
  // CALENDARIO-E2: pestana "Calendario" (agenda por tecnico) visible solo a quien
  // tiene el permiso operaciones.calendario (direccion/gerencia/operaciones); el
  // tecnico NO la ve. Gate consistente con el backend (requiereCalendario).
  const { accesoTotal, puede, usuario } = useUsuario();
  const usuarioId = usuario && usuario.id;

  // ===== LAS TRES VISTAS (EXPEDIENTES-RESUMEN FRENTE B y C, 2026-09-17) =====
  // El Resumen es SOLO DE DIRECCION y para el resto la opcion NO EXISTE: no se dibuja en gris ni
  // bloqueada, simplemente no esta. El candado de verdad vive en el servidor, que responde 403 a
  // /api/expedientes/resumen (esconder el boton no protege nada por si solo).
  // Gate por la MATRIZ GRANULAR, no por el nombre del rol (politica de permisos de CLAUDE.md):
  // direccion pasa por accesoTotal, y el dia que Ricardo quiera darselo a alguien mas lo marca en
  // la pantalla Usuarios sin tocar codigo.
  const puedeVerResumen = (accesoTotal || puede('operaciones.resumen-empresa')) && !modoSeleccion;
  const vistasDisponibles = useMemo(
    () => VISTAS.filter(v => !v.soloDireccion || puedeVerResumen),
    [puedeVerResumen]
  );
  // La vista se recuerda POR USUARIO: antes era una clave global y en un equipo compartido la
  // preferencia de uno se le pegaba al siguiente que entraba. De fabrica: direccion abre en
  // Resumen (es su foto del negocio), el resto en Tabla (es su herramienta de trabajo).
  const [vistaGuardada, setVistaGuardada] = useState(() => {
    const porUsuario = storage.get(STORAGE_KEYS.EXPEDIENTES_VISTA) || {};
    const mia = usuarioId != null ? porUsuario[String(usuarioId)] : null;
    if (mia === 'resumen' || mia === 'tabla' || mia === 'kanban') return mia;
    // Respeta la preferencia de la version anterior (clave global), que solo sabia de tabla/kanban.
    if (storage.getRaw(STORAGE_KEYS.CONTROL_EXP_VISTA) === 'kanban') return 'kanban';
    return null;   // null = "todavia no ha elegido"; lo resuelve el efecto de abajo
  });
  // Primera vez: direccion -> Resumen, el resto -> Tabla. Se hace en un efecto porque el permiso
  // llega del context y puede tardar un tick en estar disponible.
  useEffect(() => {
    if (vistaGuardada == null) setVistaGuardada(puedeVerResumen ? 'resumen' : 'tabla');
  }, [vistaGuardada, puedeVerResumen]);
  // Red de seguridad: si alguien tenia 'resumen' guardado y le quitan el permiso, cae a Tabla.
  useEffect(() => {
    if (vistaGuardada === 'resumen' && !puedeVerResumen) setVistaGuardada('tabla');
  }, [vistaGuardada, puedeVerResumen]);
  const cambiarVista = useCallback((v) => {
    setVistaGuardada(v);
    if (usuarioId == null) return;
    const porUsuario = storage.get(STORAGE_KEYS.EXPEDIENTES_VISTA) || {};
    storage.set(STORAGE_KEYS.EXPEDIENTES_VISTA, { ...porUsuario, [String(usuarioId)]: v });
  }, [usuarioId]);
  const verCalendario = (accesoTotal || puede('operaciones.calendario')) && !modoSeleccion;
  // CONTRATISTAS-EXTERNOS (2026-10-14): misma logica que el calendario, con su propia palomita.
  const verContratistas = (accesoTotal || puede('operaciones.contratistas')) && !modoSeleccion;
  const pestanas = [
    ...PESTANAS,
    ...(verCalendario ? [{ id: 'calendario', label: 'Calendario', queryTipo: null }] : []),
    ...(verContratistas ? [{ id: 'contratistas', label: 'Contratistas', queryTipo: null }] : []),
  ];
  // Hidrata pestana + filtros desde localStorage. Reset si cambio el token
  // (otro usuario inicio sesion).
  const filtrosGuardados = useMemo(() => {
    try {
      const tokenActual = localStorage.getItem('beep_token') || '';
      const tokenPrevio = localStorage.getItem(STORAGE_KEY_TOKEN_PREVIO) || '';
      if (tokenActual && tokenActual !== tokenPrevio) {
        localStorage.removeItem(STORAGE_KEY_FILTROS);
        localStorage.setItem(STORAGE_KEY_TOKEN_PREVIO, tokenActual);
        return null;
      }
      return leerFiltrosGuardados();
    } catch { return null; }
  }, []);

  // ===== STATE =====
  // EXP-ARCHIVO: migracion de preferencias. Quien tenia guardada una de las tres pestanas viejas
  // aterriza en Archivo con el filtro de Resultado ya puesto, para no perder su vista.
  const migrado = useMemo(() => migrarPestanaGuardada(filtrosGuardados), [filtrosGuardados]);
  const [pestana, setPestana] = useState(migrado.pestana);
  // Filtro "Resultado" dentro de Archivo: '' = Todos | ganado | perdido | descartado.
  const [resultadoFiltro, setResultadoFiltro] = useState(migrado.resultado);
  // A7 (MOVIL-2): "el dinero olvidado" — expedientes que todavia no tienen NINGUNA cotizacion.
  // '' = todos | 'si' = sin cotizar | '5'/'10' = sin cotizar hace mas de N dias desde el alta.
  const [sinCotizarFiltro, setSinCotizarFiltro] = useState(filtrosGuardados?.sinCotizarFiltro || '');
  // EXPEDIENTES-RESUMEN (2026-09-17): filtro por el ESTADO de la cotizacion que da el monto.
  // '' = todas | autorizada | enviada | borrador | rechazada. Es a donde llevan los renglones de
  // dinero del Resumen.
  const [cotizacionFiltro, setCotizacionFiltro] = useState(filtrosGuardados?.cotizacionFiltro || '');
  // Solo lo cerrado ESTE MES (client-side sobre `cerrado_at`). Lo enciende el bloque "Cerrado este
  // mes" del Resumen; sin el, el clic llevaria a TODO el archivo historico y la cifra no cuadraria
  // con lo que el usuario acaba de leer.
  const [cierreMes, setCierreMes] = useState(false);
  // FRENTE D: el panel de filtros (hoja inferior en celular, panel colgado del boton en escritorio).
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const botonFiltrosRef = useRef(null);
  // FRENTE D: vistas guardadas (combinaciones de filtros con nombre), por usuario.
  const [vistasGuardadas, setVistasGuardadas] = useState([]);
  useEffect(() => { setVistasGuardadas(leerVistas(usuarioId)); }, [usuarioId]);
  // EXP-ARCHIVO: en Archivo siempre se muestra la Tabla (el Kanban es de expedientes VIVOS).
  // Se deriva en vez de tocar el estado guardado, para que al volver a Activos el usuario
  // recupere el Kanban si esa era su vista preferida.
  // EXPEDIENTES-RESUMEN: el Resumen NO se pisa por la pestaña — es el total de la EMPRESA, sin
  // importar donde este el usuario (un resumen del "Archivo" no significaria nada).
  const vista = (vistaGuardada !== 'resumen' && pestana === 'archivo') ? 'tabla' : (vistaGuardada || 'tabla');
  const esArchivo = pestana === 'archivo';
  const [search, setSearch] = useState('');
  const [searchDeb, setSearchDeb] = useState('');
  // estadoFunnel guarda un codigo de estado del funnel (uno solo).
  // Si en localStorage hay un valor invalido (codigo viejo pre-migracion 011
  // o id de grupo de la version anterior con chips), lo sanitizamos a null.
  const [estadoFunnel,    setEstadoFunnel]    = useState(() => {
    const guardado = filtrosGuardados?.estadoFunnel;
    if (!guardado) return null;
    return ESTADOS_VALIDOS_FUNNEL.includes(guardado) ? guardado : null;
  });
  const [responsableId,   setResponsableId]   = useState(filtrosGuardados?.responsableId || null);
  const [clienteGrupoId,  setClienteGrupoId]  = useState(filtrosGuardados?.clienteGrupoId || null);
  const [necesidadFiltro, setNecesidadFiltro] = useState(filtrosGuardados?.necesidadFiltro || null);
  // MULTI-INTERES (2026-10-17): filtro por RUBRO (cualquiera de los del expediente, principal o no).
  // Convive con el de necesidad (lista vieja, congelada) hasta que esa se retire.
  const [familiaFiltro,   setFamiliaFiltro]   = useState(filtrosGuardados?.familiaFiltro || null);
  const familiasCat = useFamilias();
  // urgencia: viene de Mi Bandeja (KPIs vencido/esta_semana/hoy). Sin UI propia
  // en Expedientes; se hidrata desde localStorage y se aplica solo en pestana
  // activos. MiBandeja lo resetea en cada click (V2.1.1) para evitar stickiness.
  const [urgencia,        setUrgencia]        = useState(filtrosGuardados?.urgencia || null);
  // Rango de fechas sobre fecha_interes (la fecha que muestra la lista).
  // Strings 'YYYY-MM-DD' o '' (sin filtro). Se sanitizan al hidratar.
  const [fechaDesde, setFechaDesde] = useState(() => {
    const g = filtrosGuardados?.fechaDesde;
    return (g && FORMATO_FECHA_FILTRO.test(g)) ? g : '';
  });
  const [fechaHasta, setFechaHasta] = useState(() => {
    const g = filtrosGuardados?.fechaHasta;
    return (g && FORMATO_FECHA_FILTRO.test(g)) ? g : '';
  });
  // TRANSPARENCIA-TOTAL: filtros nuevos del panel fusionado (client-side sobre la
  // lista ya cargada; el backend no los conoce). Tipo de trabajo = workflow_codigo.
  const [tipoTrabajo,    setTipoTrabajo]    = useState(filtrosGuardados?.tipoTrabajo || '');
  const [semaforoFiltro, setSemaforoFiltro] = useState(filtrosGuardados?.semaforoFiltro || '');
  // Orden de la tabla (se ordenan los PADRES; los anexos quedan bajo su padre).
  const [orden, setOrden] = useState(() => {
    // EXP-ARCHIVO: quien llega migrado desde Ganados/Perdidos/Descartados aterriza ordenado por
    // fecha de cierre (lo mas reciente arriba), que es el default de la pestana Archivo.
    if (migrado.migro) return { campo: 'cerrado', dir: 'desc' };
    return (filtrosGuardados?.orden && typeof filtrosGuardados.orden === 'object')
      ? filtrosGuardados.orden
      : { campo: 'fecha', dir: 'desc' };
  });
  // EXP-ARCHIVO: al ENTRAR a Archivo el orden por defecto es "Cerrado el" descendente (lo mas
  // reciente arriba); al salir, si se venia ordenando por una columna que solo existe en Archivo,
  // se vuelve al orden normal por fecha. Solo toca el orden cuando la columna dejo de existir.
  const pestanaPrevia = useRef(pestana);
  useEffect(() => {
    if (pestanaPrevia.current === pestana) return;
    const entrando = pestana === 'archivo';
    const salio = pestanaPrevia.current === 'archivo';
    pestanaPrevia.current = pestana;
    if (entrando) setOrden({ campo: 'cerrado', dir: 'desc' });
    else if (salio) setOrden(o => (o.campo === 'cerrado' || o.campo === 'resultado' ? { campo: 'fecha', dir: 'desc' } : o));
  }, [pestana]);

  const [expedientes, setExpedientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorLista, setErrorLista] = useState(null);

  // TRANSPARENCIA-TOTAL: panel global (resumen numerico + catalogo de columnas del
  // Kanban + expedientes activos con semaforo del backend). Fuente: /api/control-expedientes
  // (ya abierto a todos). Alimenta las 4 tarjetas-resumen y la vista Kanban.
  const [panel, setPanel] = useState(null);
  // EXPEDIENTES-RESUMEN: el panel global puede responder 403 (el tecnico no tiene acceso
  // financiero). Antes eso dejaba `panel` en null para siempre y el Kanban se quedaba diciendo
  // "Cargando tablero..." eternamente; ahora se distingue "cargando" de "no puedes".
  const [panelError, setPanelError] = useState(null);
  // FRENTE B: datos del Resumen de la empresa (GET /api/expedientes/resumen, solo direccion).
  const [resumen, setResumen] = useState(null);
  const [resumenCargando, setResumenCargando] = useState(false);
  const [resumenError, setResumenError] = useState(null);
  const [avisarExp, setAvisarExp] = useState(null); // expediente para "avisar al responsable"

  // Conteos del header (5 categorias). Se calculan al inicio + tras crear.
  const [conteos, setConteos] = useState({ activos: 0, ganados: 0, perdidos: 0, descartados: 0, garantias: 0, archivados: 0 });

  // DOS-TIEMPOS: tipos-lugar y origenes-lead ya no se usan aqui (viven en el
  // wizard de contexto). Se conserva necesidades (filtro de la lista) y estados.
  const [catNecesidades,  setCatNecesidades]  = useState([]);
  const [catEstados,      setCatEstados]      = useState([]);
  const [usuarios,        setUsuarios]        = useState([]);
  const [grupos,          setGrupos]          = useState([]);

  const [modalAbierto, setModalAbierto] = useState(false);
  // DOS-TIEMPOS: tras crear, prompt "¿Agregar contexto ahora?" (no bloquea).
  // TAREAS-EXPEDIENTE: "Agregar contexto" abre la FICHA (el contexto ahora se
  // captura INLINE en su vista Tareas, ya no hay wizard emergente aqui).
  const [contextoPrompt, setContextoPrompt] = useState(null); // fila recien creada
  const [guardando,    setGuardando]    = useState(false);
  const [errorForm,    setErrorForm]    = useState(null);
  const [toast,        setToast]        = useState(null);
  // TIEMPO-REAL: hay una edicion inline (estado/titulo/responsable) escribiendo en el
  // backend -> pausa el refresco automatico para no pisar la actualizacion optimista.
  const [guardandoInline, setGuardandoInline] = useState(false);

  // Seleccion -> abre FichaExpediente en pantalla completa (patron ClientesNuevo)
  const [seleccionId, setSeleccionId] = useState(null);

  // DOS-TIEMPOS: el form del alta quedo en lo MINIMO (tipo, titulo, cliente,
  // ubicacion). El contexto (origen/lugar/necesidad/notas) vive en el wizard.
  const [form, setForm] = useState({
    titulo: '',
    cliente_id: null,
    cliente_grupo_id: null,
    cliente_razon_social_id: null,
    cliente_sucursal_id: null,
    cliente_persona_id: null,
    _cliente_resumen: '',
    _cliente_detalle: null,
    _cliente_sin_vinculo: false,
  });

  // ===== EFFECTS =====

  // Cargar catalogos + usuarios + grupos una sola vez
  useEffect(() => {
    let activo = true;
    Promise.all([
      fetch(`${API}/api/catalogos/tipos-necesidad`).then(r => r.json()).catch(() => []),
      fetch(`${API}/api/catalogos/estados-oportunidad`).then(r => r.json()).catch(() => []),
      // /api/usuarios requiere auth — el mismo patron que el resto del front:
      // si falla por 401, simplemente queda lista vacia y el dropdown se oculta.
      fetch(`${API}/api/usuarios`, { headers: authHeaders() }).then(r => r.json()).catch(() => ({ usuarios: [] })),
      fetch(`${API}/api/clientes/grupos`, { headers: authHeaders() }).then(r => r.json()).catch(() => ({ grupos: [] })),
    ]).then(([tn, eo, us, gr]) => {
      if (!activo) return;
      setCatNecesidades(Array.isArray(tn) ? tn : []);
      setCatEstados(Array.isArray(eo)     ? eo : []);
      setUsuarios(Array.isArray(us?.usuarios) ? us.usuarios : []);
      setGrupos(Array.isArray(gr?.grupos) ? gr.grupos : []);
    });
    return () => { activo = false; };
  }, []);

  // Persistir filtros + pestana
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTROS, JSON.stringify({
        pestana, resultadoFiltro, sinCotizarFiltro, estadoFunnel, responsableId, clienteGrupoId, necesidadFiltro, familiaFiltro, urgencia,
        fechaDesde, fechaHasta, tipoTrabajo, semaforoFiltro, cotizacionFiltro, orden,
      }));
    } catch {}
  }, [pestana, resultadoFiltro, sinCotizarFiltro, estadoFunnel, responsableId, clienteGrupoId, necesidadFiltro, familiaFiltro, urgencia, fechaDesde, fechaHasta, tipoTrabajo, semaforoFiltro, cotizacionFiltro, orden]);

  // Debounce buscador
  useEffect(() => {
    const t = setTimeout(() => setSearchDeb(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Cargar lista cuando cambia pestana / filtros / busqueda
  // `silencioso`: TIEMPO-REAL FASE 1. En refresco automatico NO se toca el spinner ni el
  // error ni se vacia la lista en caso de fallo (cero parpadeo; reintenta al proximo ciclo).
  // El spinner queda solo para la carga inicial / cambio de filtros (silencioso = false).
  const cargarLista = useCallback(async ({ silencioso = false } = {}) => {
    // ACTIVIDAD-GLOBAL: esa pestana trae su propio fetch (no lista expedientes).
    if (pestana === 'actividad' || pestana === 'calendario' || pestana === 'contratistas') { setLoading(false); return; }
    if (!silencioso) { setLoading(true); setErrorLista(null); }
    try {
      const def = PESTANAS.find(p => p.id === pestana) || PESTANAS[0];
      const qs = new URLSearchParams();
      // EXP-ARCHIVO: dentro de Archivo, el chip de Resultado se traduce al mismo parametro `tipo`
      // (el backend ya acepta 'archivo' y la lista por comas). Sin Resultado -> todo lo cerrado.
      if (pestana === 'archivo' && resultadoFiltro) {
        const alias = { ganado: 'ganados', perdido: 'perdidos', descartado: 'descartados' }[resultadoFiltro];
        qs.set('tipo', alias || 'archivo');
      } else {
        qs.set('tipo', def.queryTipo);
      }
      if (pestana === 'activos' && estadoFunnel) qs.set('estado', estadoFunnel);
      if (pestana === 'activos' && urgencia)     qs.set('urgencia', urgencia);
      // 'sin' = expedientes sin responsable asignado. NO se manda al backend (espera un id
      // numerico); se filtra en el cliente, mas abajo, junto al resto de filtros locales.
      if (responsableId && responsableId !== 'sin') qs.set('responsable_id', responsableId);
      if (clienteGrupoId)  qs.set('cliente_grupo_id', clienteGrupoId);
      if (necesidadFiltro) qs.set('necesidad', necesidadFiltro);
      if (familiaFiltro)   qs.set('familia', familiaFiltro);
      if (fechaDesde)      qs.set('fecha_desde', fechaDesde);
      if (fechaHasta)      qs.set('fecha_hasta', fechaHasta);
      if (searchDeb)       qs.set('search', searchDeb);
      const r = await fetch(`${API}/api/oportunidades?${qs.toString()}`, { headers: authHeaders() }).then(x => x.json());
      if (!Array.isArray(r)) throw new Error(r?.error || 'Respuesta invalida');
      // Pestana garantias: filtro local por tipo_expediente
      const filtrados = (pestana === 'garantias')
        ? r.filter(e => e.tipo_expediente === 'garantia')
        : r;
      setExpedientes(filtrados);
    } catch (e) {
      if (!silencioso) { setErrorLista(e.message); setExpedientes([]); }
      // silencioso: dejar la lista actual intacta y reintentar en el siguiente ciclo
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [pestana, resultadoFiltro, estadoFunnel, urgencia, responsableId, clienteGrupoId, necesidadFiltro, familiaFiltro, fechaDesde, fechaHasta, searchDeb]);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  // Conteos para el header (5 categorias). Se hace una sola pasada con
  // tipo=todos y se cuenta en frontend. Limit del backend es 200 — si la
  // base crece mucho los conteos se truncaran, pero para el tamano actual
  // del CRM es suficiente. Se refresca al crear un expediente nuevo.
  const refrescarConteos = useCallback(async () => {
    try {
      // EXP-ARCHIVO: los conteos vienen AGREGADOS de la BD. Antes se contaba en el cliente sobre
      // `tipo=todos`, que trae como maximo 200 filas: pasado ese tope los numeros del encabezado
      // salian truncados. Si el endpoint no responde, se cae al metodo viejo (mejor algo que nada).
      const j = await fetch(`${API}/api/oportunidades/conteos`, { headers: authHeaders() }).then(x => x.json());
      if (j && j.ok && j.conteos) {
        const c = j.conteos;
        setConteos({
          activos: c.activos || 0, ganados: c.ganados || 0, perdidos: c.perdidos || 0,
          descartados: c.descartados || 0, garantias: c.garantias || 0,
          archivados: c.archivados != null ? c.archivados : (c.ganados || 0) + (c.perdidos || 0) + (c.descartados || 0),
        });
        return;
      }
      throw new Error('sin conteos');
    } catch {
      try {
        const r = await fetch(`${API}/api/oportunidades?tipo=todos`, { headers: authHeaders() }).then(x => x.json());
        if (!Array.isArray(r)) return;
        const c = { activos: 0, ganados: 0, perdidos: 0, descartados: 0, garantias: 0, archivados: 0 };
        for (const o of r) {
          if (o.tipo_cierre == null) c.activos++;
          else if (o.tipo_cierre === 'ganado') c.ganados++;
          else if (o.tipo_cierre === 'perdido') c.perdidos++;
          else if (o.tipo_cierre === 'descartado') c.descartados++;
          if (o.tipo_expediente === 'garantia') c.garantias++;
        }
        c.archivados = c.ganados + c.perdidos + c.descartados;
        setConteos(c);
      } catch {}
    }
  }, []);
  useEffect(() => { refrescarConteos(); }, [refrescarConteos]);

  // TRANSPARENCIA-TOTAL: panel global (resumen + columnas del kanban + activos con
  // semaforo). Endpoint abierto a todos. Si falla (red/permiso) dejamos panel en
  // null y las tarjetas muestran "—"; la tabla sigue funcionando por su cuenta.
  const cargarPanel = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/api/control-expedientes`, { headers: authHeaders() });
      const r = await resp.json().catch(() => null);
      if (r && r.ok) { setPanel(r); setPanelError(null); return; }
      // 403 = el usuario no tiene acceso financiero (rol tecnico). Se dice, en vez de dejar un
      // "Cargando tablero..." que no termina nunca.
      setPanelError(resp.status === 403
        ? 'El tablero incluye montos, y tu perfil no tiene acceso a informacion financiera.'
        : ((r && r.error) || 'No se pudo cargar el tablero.'));
    } catch { /* silencioso: la tabla no depende del panel */ }
  }, []);
  useEffect(() => { cargarPanel(); }, [cargarPanel]);

  // EXPEDIENTES-RESUMEN (FRENTE B): el Resumen de la empresa. Solo se pide si el usuario puede
  // verlo; aun asi, el servidor responde 403 por su cuenta a quien no deba (el candado es suyo,
  // no de esta pantalla).
  const cargarResumen = useCallback(async () => {
    if (!puedeVerResumen) return;
    setResumenCargando(true);
    try {
      const resp = await fetch(`${API}/api/expedientes/resumen`, { headers: authHeaders() });
      const r = await resp.json().catch(() => null);
      if (r && r.ok) { setResumen(r); setResumenError(null); }
      else setResumenError((r && r.error) || `HTTP ${resp.status}`);
    } catch (e) {
      setResumenError(e.message || 'No se pudo cargar');
    } finally {
      setResumenCargando(false);
    }
  }, [puedeVerResumen]);
  useEffect(() => { if (vista === 'resumen') cargarResumen(); }, [vista, cargarResumen]);

  // TIEMPO-REAL FASE 1: recarga silenciosa (lista + conteos) reutilizando el hook
  // useAutoRefresh. Mismo fetch que la carga normal, sin spinner. Se PAUSA mientras el
  // usuario edita inline, tiene el modal abierto, o esta dentro de una ficha (detalle):
  // el refresco no debe pisar su trabajo. Keys estables (por id) -> un refresco no
  // remonta filas ni cierra un menu/editor inline abierto.
  const recargaSilenciosa = useCallback(async () => {
    await cargarLista({ silencioso: true });
    refrescarConteos();
    cargarPanel();
    if (vista === 'resumen') cargarResumen();
  }, [cargarLista, refrescarConteos, cargarPanel, cargarResumen, vista]);

  useAutoRefresh(recargaSilenciosa, {
    intervaloMs: 20000,
    pausado: modalAbierto || seleccionId != null || guardandoInline
      || contextoPrompt != null,
  });

  // CO-LINK-EXPEDIENTE: abrir la ficha de un expediente concreto cuando el editor de
  // cotizacion pide "Ir al expediente" (Dashboard pasa aperturaInicialId). Se consume
  // una vez (onAperturaConsumida limpia el id en Dashboard) para no reabrir al cerrar.
  useEffect(() => {
    if (aperturaInicialId != null) {
      setSeleccionId(aperturaInicialId);
      if (onAperturaConsumida) onAperturaConsumida();
    }
  }, [aperturaInicialId, onAperturaConsumida]);

  // ===== DERIVADOS =====

  const opcionesResponsable = useMemo(() => ([
    // EXPEDIENTES-RESUMEN: "Sin responsable" es un filtro de verdad — el Resumen lo cuenta y hay
    // que poder ir a verlos. Se filtra en el cliente (el backend espera un id numerico).
    { value: 'sin', label: 'Sin responsable' },
    ...usuarios
      .filter(u => u.activo !== false && u.estado !== 'archivado')
      .map(u => ({ value: u.id, label: u.nombre })),
  ]), [usuarios]);

  const opcionesGrupo = useMemo(() => (
    grupos.map(g => ({ value: g.id, label: g.nombre }))
  ), [grupos]);

  const opcionesNecesidad = useMemo(() => (
    catNecesidades
      .filter(n => n.activo !== false)
      .map(n => ({ value: n.codigo, label: `${n.icono || ''} ${n.nombre}`.trim() }))
  ), [catNecesidades]);
  const opcionesFamilia = useMemo(() => familiasCat.map(f => ({ value: f.id, label: f.etiqueta })), [familiasCat]);

  const formularioValido = useMemo(() => {
    // EXP-CLIENTE-CUALQUIERA: debe haber un cliente vinculado (de cualquier
    // tipo), salvo que se marque explicitamente "crear sin cliente vinculado".
    const tieneCliente = !!(
      form.cliente_grupo_id || form.cliente_razon_social_id ||
      form.cliente_sucursal_id || form.cliente_persona_id
    );
    if (!tieneCliente && !form._cliente_sin_vinculo) return false;
    // La sucursal solo es obligatoria si el vinculo es por GRUPO (su direccion
    // alimenta el proyecto). Empresa/persona no la requieren.
    if (form.cliente_grupo_id && !form.cliente_sucursal_id) return false;
    // EXP-TITULO: titulo obligatorio (min 3 chars).
    if (!form.titulo || form.titulo.trim().length < 3) return false;
    // FLUJOS-FIJOS: el tipo de trabajo es obligatorio (decide el flujo).
    if (!form.tipo_trabajo) return false;
    // DOS-TIEMPOS (2026-07-09): tipo_lugar/origen/necesidad YA NO son
    // obligatorios al crear — viven en el Tiempo 2 (contexto por rondas).
    return true;
  }, [form]);

  // EXPEDIENTES-RESUMEN (FRENTE D): ademas de saber SI hay filtros, hay que saber CUANTOS, porque
  // el boton "Filtros" lleva el contador. La busqueda por texto NO cuenta: tiene su propia caja a
  // la vista y no esta escondida detras del boton.
  const filtrosActuales = useMemo(() => ({
    tipoTrabajo, semaforoFiltro, sinCotizarFiltro, cotizacionFiltro, resultadoFiltro,
    estadoFunnel, responsableId, clienteGrupoId, necesidadFiltro, familiaFiltro, fechaDesde, fechaHasta, cierreMes,
  }), [tipoTrabajo, semaforoFiltro, sinCotizarFiltro, cotizacionFiltro, resultadoFiltro,
      estadoFunnel, responsableId, clienteGrupoId, necesidadFiltro, familiaFiltro, fechaDesde, fechaHasta, cierreMes]);

  // Las fechas cuentan como UN filtro aunque sean dos campos (es un rango, se lee como uno).
  const conteoFiltros = useMemo(() => {
    let n = 0;
    for (const [k, v] of Object.entries(filtrosActuales)) {
      if (k === 'fechaHasta') continue;                 // ya contado con fechaDesde
      if (k === 'fechaDesde') { if (fechaDesde || fechaHasta) n++; continue; }
      if (v !== '' && v != null && v !== false) n++;
    }
    return n;
  }, [filtrosActuales, fechaDesde, fechaHasta]);
  const hayFiltrosAvanzados = conteoFiltros > 0;

  // EXPEDIENTES-RESUMEN (FRENTE D): lo que hay puesto, en etiquetas con una X. Antes el resumen de
  // filtros solo cubria los cinco dropdowns "avanzados"; los chips de Tipo / Avance / Sin cotizar /
  // Resultado se quedaban fuera, asi que se podia tener un filtro puesto sin verlo por ningun lado.
  // Ahora que los filtros viven PLEGADOS, eso seria un agujero: si no se ve en la etiqueta, no se
  // ve en ningun sitio. Estan los diez.
  const chipsActivos = useMemo(() => {
    const out = [];
    const etiqueta = (lista, id) => (lista.find(x => String(x.id) === String(id)) || {}).label || id;
    if (tipoTrabajo)      out.push({ id: 'tipo',   label: `Tipo: ${etiqueta(TIPOS_TRABAJO_FILTRO, tipoTrabajo)}`,  color: '#2f5f9e', quitar: () => setTipoTrabajo('') });
    if (semaforoFiltro)   out.push({ id: 'avance', label: `Avance: ${etiqueta(SEMAFORO_FILTRO, semaforoFiltro)}`,  color: (SEMAFORO[semaforoFiltro] || {}).color || '#5e666e', quitar: () => setSemaforoFiltro('') });
    if (sinCotizarFiltro) out.push({ id: 'sincot', label: sinCotizarFiltro === 'si' ? 'Sin cotizar' : `Sin cotizar +${sinCotizarFiltro} dias`, color: '#d97706', quitar: () => setSinCotizarFiltro('') });
    if (cotizacionFiltro) out.push({ id: 'cotiz',  label: `Cotizacion: ${etiqueta(COTIZACION_FILTRO, cotizacionFiltro)}`, color: (COTIZACION_FILTRO.find(x => x.id === cotizacionFiltro) || {}).color || '#5e666e', quitar: () => setCotizacionFiltro('') });
    if (resultadoFiltro)  out.push({ id: 'result', label: `Resultado: ${(CIERRE_VISUAL[resultadoFiltro] || {}).label || resultadoFiltro}`, color: (CIERRE_VISUAL[resultadoFiltro] || {}).color || '#5e666e', quitar: () => setResultadoFiltro('') });
    if (estadoFunnel) {
      const meta = ESTADO_VISUAL_OPORTUNIDAD[estadoFunnel] || {};
      out.push({ id: 'estado', label: `Paso: ${meta.label || estadoFunnel}`, color: meta.color || '#5e666e', quitar: () => setEstadoFunnel(null) });
    }
    if (responsableId) {
      const o = opcionesResponsable.find(x => String(x.value) === String(responsableId));
      out.push({ id: 'resp', label: `Responsable: ${o ? o.label : responsableId}`, color: '#185FA5', quitar: () => setResponsableId(null) });
    }
    if (clienteGrupoId) {
      const o = opcionesGrupo.find(x => String(x.value) === String(clienteGrupoId));
      out.push({ id: 'grupo', label: `Cliente: ${o ? o.label : clienteGrupoId}`, color: '#185FA5', quitar: () => setClienteGrupoId(null) });
    }
    if (necesidadFiltro) {
      const o = opcionesNecesidad.find(x => String(x.value) === String(necesidadFiltro));
      out.push({ id: 'neces', label: `Necesidad: ${o ? o.label : necesidadFiltro}`, color: '#185FA5', quitar: () => setNecesidadFiltro(null) });
    }
    if (familiaFiltro) {
      const o = opcionesFamilia.find(x => String(x.value) === String(familiaFiltro));
      out.push({ id: 'rubro', label: `Rubro: ${o ? o.label : familiaFiltro}`, color: '#0f766e', quitar: () => setFamiliaFiltro(null) });
    }
    if (fechaDesde || fechaHasta) {
      const txt = fechaDesde && fechaHasta
        ? `${etiquetaFechaFiltro(fechaDesde)} a ${etiquetaFechaFiltro(fechaHasta)}`
        : (fechaDesde ? `Desde ${etiquetaFechaFiltro(fechaDesde)}` : `Hasta ${etiquetaFechaFiltro(fechaHasta)}`);
      out.push({ id: 'fechas', label: txt, color: '#185FA5', quitar: () => { setFechaDesde(''); setFechaHasta(''); } });
    }
    if (cierreMes) out.push({ id: 'mes', label: 'Cerrado este mes', color: '#16a34a', quitar: () => setCierreMes(false) });
    return out;
  }, [tipoTrabajo, semaforoFiltro, sinCotizarFiltro, cotizacionFiltro, resultadoFiltro, estadoFunnel,
      responsableId, clienteGrupoId, necesidadFiltro, fechaDesde, fechaHasta, cierreMes,
      opcionesResponsable, opcionesGrupo, opcionesNecesidad]);

  // ===== HANDLERS =====
  const abrirModal = () => {
    setForm({
      // FLUJOS-FIJOS: primer campo del alta. Decide el flujo del expediente.
      tipo_trabajo: '',
      titulo: '',
      // CATALOGO-FAMILIAS: de que tecnologia es. Vacio = se deduce del titulo.
      familia: '',
      cliente_id: null, cliente_grupo_id: null, cliente_razon_social_id: null,
      cliente_sucursal_id: null, cliente_persona_id: null,
      _cliente_resumen: '', _cliente_detalle: null, _cliente_sin_vinculo: false,
    });
    setErrorForm(null);
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    if (guardando) return;
    setModalAbierto(false);
    setErrorForm(null);
  };

  const setCampo = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const limpiarFiltros = () => {
    setEstadoFunnel(null);
    setResponsableId(null);
    setClienteGrupoId(null);
    setNecesidadFiltro(null);
    setFamiliaFiltro(null);
    setFechaDesde('');
    setFechaHasta('');
    setTipoTrabajo('');
    setSemaforoFiltro('');
    setResultadoFiltro(''); // EXP-ARCHIVO: "Limpiar" devuelve el Archivo a Todos
    setSinCotizarFiltro('');
    setCotizacionFiltro('');
    setCierreMes(false);
  };

  // EXPEDIENTES-RESUMEN (FRENTE B): un renglon del Resumen lleva a la lista YA FILTRADA. Se parte
  // de cero (se limpia todo) y se pone exactamente lo que ese renglon representa: si no, el
  // usuario aterrizaria con filtros viejos encima y el numero no cuadraria con el que acaba de
  // leer, que es peor que no llevarle a ningun sitio.
  const irALista = useCallback((f = {}) => {
    setEstadoFunnel(null); setResponsableId(null); setClienteGrupoId(null); setNecesidadFiltro(null); setFamiliaFiltro(null);
    setFechaDesde(''); setFechaHasta(''); setTipoTrabajo(''); setSemaforoFiltro('');
    setResultadoFiltro(''); setSinCotizarFiltro(''); setCotizacionFiltro(''); setCierreMes(false);
    setSearch('');
    if (f.pestana)          setPestana(f.pestana);
    if (f.semaforoFiltro)   setSemaforoFiltro(f.semaforoFiltro);
    if (f.sinCotizarFiltro) setSinCotizarFiltro(f.sinCotizarFiltro);
    if (f.cotizacionFiltro) setCotizacionFiltro(f.cotizacionFiltro);
    if (f.resultadoFiltro)  setResultadoFiltro(f.resultadoFiltro);
    if (f.responsableId)    setResponsableId(f.responsableId);
    if (f.cierreMes)        setCierreMes(true);
    cambiarVista('tabla');
  }, [cambiarVista]);

  // FRENTE D — vistas guardadas.
  const aplicarVista = useCallback((v) => {
    const f = (v && v.filtros) || {};
    setTipoTrabajo(f.tipoTrabajo || '');
    setSemaforoFiltro(f.semaforoFiltro || '');
    setSinCotizarFiltro(f.sinCotizarFiltro || '');
    setCotizacionFiltro(f.cotizacionFiltro || '');
    setResultadoFiltro(f.resultadoFiltro || '');
    setEstadoFunnel(f.estadoFunnel || null);
    setResponsableId(f.responsableId || null);
    setClienteGrupoId(f.clienteGrupoId || null);
    setNecesidadFiltro(f.necesidadFiltro || null);
    setFamiliaFiltro(f.familiaFiltro || null);
    setFechaDesde(f.fechaDesde || '');
    setFechaHasta(f.fechaHasta || '');
    setCierreMes(!!f.cierreMes);
  }, []);

  const mostrarToast = (msg, tipo = 'ok') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3500);
  };

  const guardar = async () => {
    if (!formularioValido || guardando) return;
    setGuardando(true);
    setErrorForm(null);
    try {
      const payload = { ...form };
      delete payload._cliente_resumen;
      delete payload._cliente_detalle;
      delete payload._cliente_sin_vinculo;
      for (const k of Object.keys(payload)) {
        if (payload[k] === '') payload[k] = null;
      }
      const r = await fetch(`${API}/api/oportunidades`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      }).then(x => x.json());
      if (r?.error) throw new Error(r.error);
      if (!r?.id) throw new Error('Respuesta sin id');
      mostrarToast(`Expediente ${r.folio} creado`);
      setModalAbierto(false);
      cargarLista();
      refrescarConteos();
      // DOS-TIEMPOS: ofrecer el contexto SIN bloquear (el expediente ya existe).
      setContextoPrompt(r);
    } catch (e) {
      setErrorForm(e.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const onClickCard = (exp) => {
    // ROUND-TRIP cotizacion: en modo seleccion, el click DEVUELVE el expediente
    // elegido al editor (no abre el detalle).
    if (modoSeleccion && typeof onElegir === 'function') {
      onElegir({
        id: exp.id,
        folio: exp.folio || '',
        cliente: exp.cliente_grupo_nombre || exp.cliente_nombre || 'Expediente',
        proyecto: exp.titulo || exp.descripcion_necesidad || '',
      });
      return;
    }
    setSeleccionId(exp.id);
  };

  // EXP-LISTA-V2: edicion inline de titulo/estado/responsable desde la lista.
  // Hace PUT parcial y refresca la lista. Devuelve true si OK, false si fallo.
  // FIX-EXP-V2.1: enriquecer la respuesta del PUT con campos derivados
  // (estado_nombre, estado_color, responsable_nombre) que el backend NO
  // devuelve en el JOIN; sin esto la fila no se refresca visualmente.
  const actualizarExpedienteInline = useCallback(async (id, cambios) => {
    setGuardandoInline(true); // pausa el refresco automatico mientras se escribe el cambio
    try {
      const r = await fetch(`${API}/api/oportunidades/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(cambios),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        mostrarToast('Error: ' + (data?.error || `HTTP ${r.status}`), 'error');
        return false;
      }
      // Recalcular campos derivados localmente para que la fila se refresque
      // sin necesidad de refetch.
      if (cambios.estado && data) {
        const meta = (catEstados || []).find(x => x.codigo === cambios.estado);
        if (meta) {
          data.estado_nombre = meta.nombre;
          data.estado_color  = meta.color;
        }
      }
      if (cambios.vendedor_id !== undefined && data) {
        if (cambios.vendedor_id === null) {
          data.responsable_nombre = null;
        } else {
          const u = (usuarios || []).find(x => x.id === cambios.vendedor_id);
          if (u) data.responsable_nombre = u.nombre;
        }
      }
      setExpedientes(prev => prev.map(e => e.id === id ? { ...e, ...data } : e));
      return true;
    } catch (e) {
      mostrarToast('Error: ' + (e.message || 'no se pudo guardar'), 'error');
      return false;
    } finally {
      setGuardandoInline(false);
    }
  }, [catEstados, usuarios]);

  // EXP-LISTA: agrupar expedientes en padres + anexos para vista jerarquica.
  // Si un anexo cumple los filtros pero su padre NO esta en la lista actual,
  // se promueve a "raiz" (huerfano visual) para que el usuario lo vea.
  // TRANSPARENCIA-TOTAL: filtros client-side sobre la lista ya cargada (el backend
  // no conoce tipo de trabajo ni semaforo). Se aplican ANTES de agrupar.
  const expedientesVisibles = useMemo(() => (
    expedientes.filter(e => {
      if (tipoTrabajo && e.workflow_codigo !== tipoTrabajo) return false;
      if (semaforoFiltro && semaforoDe(e.dias_en_paso, panel?.umbrales) !== semaforoFiltro) return false;
      // A7: "sin cotizar" = no hay NINGUNA cotizacion capturada (monto sin resolver desde el backend).
      // Con umbral, ademas se exige que lleve mas de N dias asi, contados desde el ALTA.
      if (sinCotizarFiltro) {
        // OJO: "sin cotizar" se reconoce por `monto_estado`, no por `monto`. Desde
        // EXPEDIENTES-RESUMEN el backend manda el monto VACIO cuando es de otra persona, y leer
        // ese hueco como "sin cotizar" marcaria como olvidados expedientes que si estan cotizados.
        // `monto_estado` si viaja siempre (dice SI hay cotizacion y en que estado, nunca cuanto).
        if (e.monto_estado) return false;
        const n = Number(sinCotizarFiltro);
        if (Number.isFinite(n) && n > 0) {
          const d = diasDesde(e.creado_at);
          if (d == null || d < n) return false;
        }
      }
      // EXPEDIENTES-RESUMEN: estado de la cotizacion que da el monto (destino de los renglones
      // de dinero del Resumen).
      if (cotizacionFiltro && e.monto_estado !== cotizacionFiltro) return false;
      // "Sin responsable": se filtra aqui porque el backend espera un id numerico.
      if (responsableId === 'sin' && e.vendedor_id != null) return false;
      // Solo lo cerrado ESTE MES (llega del bloque "Cerrado este mes" del Resumen).
      if (cierreMes) {
        const v = e.cerrado_at || e.fecha_cierre;
        if (!v) return false;
        const d = new Date(v);
        const hoy = new Date();
        if (isNaN(d.getTime())) return false;
        if (d.getFullYear() !== hoy.getFullYear() || d.getMonth() !== hoy.getMonth()) return false;
      }
      return true;
    })
  ), [expedientes, tipoTrabajo, semaforoFiltro, sinCotizarFiltro, cotizacionFiltro, responsableId, cierreMes, panel]);

  const { padres, anexosPorPadre } = useMemo(() => {
    const idsEnLista = new Set(expedientesVisibles.map(e => e.id));
    const raices = [];
    const porPadre = {};
    for (const e of expedientesVisibles) {
      if (e.expediente_padre_id && idsEnLista.has(e.expediente_padre_id)) {
        if (!porPadre[e.expediente_padre_id]) porPadre[e.expediente_padre_id] = [];
        porPadre[e.expediente_padre_id].push(e);
      } else {
        raices.push(e);
      }
    }
    // Orden configurable de los PADRES (los anexos quedan bajo su padre, por fecha).
    const dir = orden.dir === 'asc' ? 1 : -1;
    raices.sort((a, b) => {
      const va = valorOrdenExpediente(a, orden.campo);
      const vb = valorOrdenExpediente(b, orden.campo);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    Object.keys(porPadre).forEach(k => {
      porPadre[k].sort((a, b) => new Date(a.fecha_interes || 0) - new Date(b.fecha_interes || 0));
    });
    return { padres: raices, anexosPorPadre: porPadre };
  }, [expedientesVisibles, orden]);

  const toggleOrden = (campo) => {
    setOrden(o => o.campo === campo
      ? { campo, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo, dir: (campo === 'dias' || campo === 'monto' || campo === 'fecha') ? 'desc' : 'asc' });
  };

  // ===== KANBAN (vista secundaria) — reusa panel + VistaKanban del control global =====
  const columnasKanban = useMemo(() => {
    if (!panel) return [];
    if (tipoTrabajo) {
      const wf = (panel.workflows || []).find(w => w.codigo === tipoTrabajo);
      return wf ? wf.pasos.map(p => ({ key: p.estado_codigo, label: p.nombre })) : [];
    }
    return (panel.etapasGenericas || []).map(e => ({ key: e.id, label: e.label }));
  }, [panel, tipoTrabajo]);
  const claveColumnaKanban = useCallback(
    (exp) => (tipoTrabajo ? exp.estado : exp.etapa_generica),
    [tipoTrabajo]
  );
  const expedientesKanban = useMemo(() => {
    const q = searchDeb.trim().toLowerCase();
    return (panel?.expedientes || []).filter(e => {
      if (tipoTrabajo && e.workflow_codigo !== tipoTrabajo) return false;
      if (semaforoFiltro && e.semaforo !== semaforoFiltro) return false;
      if (responsableId === 'sin') { if (e.vendedor_id != null) return false; }
      else if (responsableId && String(e.vendedor_id) !== String(responsableId)) return false;
      if (cotizacionFiltro && e.monto_estado !== cotizacionFiltro) return false;
      if (sinCotizarFiltro && e.monto_estado) return false;
      if (q) {
        const hay = [e.folio, e.cliente_nombre, e.cliente_jerarquia_corta, e.responsable_nombre, e.titulo]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [panel, tipoTrabajo, semaforoFiltro, responsableId, cotizacionFiltro, sinCotizarFiltro, searchDeb]);

  // ===== ESTILOS BASE =====
  const sectionPad = esCelular ? 16 : 28;
  const cols = esCelular ? 1 : 3;

  // Pantalla completa: si hay un expediente seleccionado, abrimos FichaExpediente.
  // Patron exacto al de ClientesNuevo.js con sus Ficha*. Los hooks de arriba ya
  // corrieron, asi que esta rama condicional respeta las reglas de React.
  if (seleccionId != null) {
    return (
      <FichaExpediente
        id={seleccionId}
        onVolver={() => {
          setSeleccionId(null);
          cargarLista();
          refrescarConteos();
        }}
        onCambiarId={(otroId) => setSeleccionId(otroId)}
        esCelular={esCelular}
      />
    );
  }

  // ===== RENDER =====
  return (
    <div style={{
      fontFamily: FF, color: COLOR_GRIS, background: COLOR_FONDO,
      minHeight: '100%', padding: sectionPad,
    }}>
      {/* Estilos compartidos con el Kanban/tarjetas reusados del panel global.
          Se replican aqui porque ControlExpedientes ya no se monta como pantalla. */}
      <style>{`
        @keyframes beepShimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes beepFadeUp { from{opacity:0; transform:translateY(6px)} to{opacity:1; transform:translateY(0)} }
        .beep-skel { background:linear-gradient(90deg,#eceef0 25%,#f6f7f8 50%,#eceef0 75%); background-size:800px 100%; animation:beepShimmer 1.2s infinite linear; border-radius:8px; }
        .beep-card-exp { transition: box-shadow .18s ease-out, transform .18s ease-out, border-color .18s ease-out; -webkit-tap-highlight-color: transparent; }
        .beep-card-exp:hover { box-shadow:0 6px 18px rgba(31,41,55,.10); transform:translateY(-1px); border-color:#cdd4dd; }
        .beep-card-exp:active { transform:scale(.99); }
        .beep-fila-exp { transition: background .15s ease-out; cursor:pointer; }
        .beep-fila-exp:hover { background:#f7f9fb; }
        .beep-chip { transition: all .15s ease-out; -webkit-tap-highlight-color: transparent; cursor:pointer; }
        /* KANBAN-SCROLL: la barra horizontal del carril es VISIBLE a proposito (afordancia:
           es la unica pista de que hay mas columnas). No ocultarla. */
        .beep-kanban-scroll { scrollbar-width: auto; scrollbar-color:#b6bec9 #eef0f3; }
        .beep-kanban-scroll::-webkit-scrollbar { height:12px; width:10px; }
        .beep-kanban-scroll::-webkit-scrollbar-track { background:#eef0f3; border-radius:8px; }
        .beep-kanban-scroll::-webkit-scrollbar-thumb { background:#b6bec9; border-radius:8px; border:2px solid #eef0f3; }
        .beep-kanban-scroll::-webkit-scrollbar-thumb:hover { background:#98a3b2; }
        .beep-kanban-rail { cursor:grab; }
        .beep-kanban-rail:active { cursor:grabbing; }
        /* Scroll vertical de cada columna: discreto, para no competir con el horizontal. */
        .beep-kanban-col { scrollbar-width: thin; scrollbar-color:#d5dae1 transparent; }
        .beep-kanban-col::-webkit-scrollbar { width:8px; }
        .beep-kanban-col::-webkit-scrollbar-thumb { background:#d5dae1; border-radius:8px; }
        .beep-kanban-col::-webkit-scrollbar-track { background:transparent; }
      `}</style>
      {/* ROUND-TRIP cotizacion: aviso de modo seleccion + Cancelar. El click en una
          fila vincula ese expediente a la cotizacion y regresa al editor. */}
      {modoSeleccion && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          padding: '12px 16px', marginBottom: 16, borderRadius: 10,
          background: '#e6f1fb', border: `1px solid ${COLOR_BEEP_AZUL}`,
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO }}>
            {etiquetaSeleccion || `Eligiendo expediente para la cotizacion ${seleccionFolio}.`} Toca un expediente para elegirlo.
          </span>
          <button
            onClick={() => { if (typeof onCancelarSeleccion === 'function') onCancelarSeleccion(); }}
            style={{
              flexShrink: 0, background: '#fff', color: COLOR_OSCURO, border: `1px solid ${COLOR_BORDE}`,
              borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 700, fontFamily: FF,
              cursor: 'pointer', minHeight: 44,
            }}
          >Cancelar</button>
        </div>
      )}
      {/* ============ CABECERA — EXPEDIENTES-RESUMEN FRENTE D (2026-09-17) ============
          Titulo + la linea de conteos, y se acabo.

          LAS CUATRO TARJETAS DE ARRIBA SE RETIRARON ("Expedientes activos / Atorados / Valor en
          curso / Cerrados este mes"). Dos motivos, y los dos son de Ricardo:
            1. "Valor en curso" enseñaba la facturacion completa de la empresa a CUALQUIERA que
               entrara a Expedientes. Con el Resumen vuelto privado, dejarlas ahi habria sido
               cerrar una puerta con la otra abierta.
            2. "Atorados" no informaba de nada: con 163 de 168 en rojo, ese numero no significa
               nada. Un semaforo donde todo esta en rojo es un semaforo apagado.
          Lo que valia de ellas vive ahora en el Resumen: desglosado, explicado y con candado. */}
      <div style={{ marginBottom: 14 }}>
        <h1 style={{
          fontFamily: FF, color: COLOR_OSCURO,
          fontSize: esCelular ? 22 : 28, fontWeight: 700,
          margin: 0, lineHeight: 1.1,
        }}>Expedientes</h1>
        <div style={{ marginTop: 5, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5 }}>
          <strong style={{ color: COLOR_BEEP_AZUL }}>{conteos.activos}</strong> activos
          <span style={{ color: COLOR_GRIS_CL }}> &middot; </span>
          <strong
            style={{ color: COLOR_OSCURO, cursor: 'help' }}
            title={`${conteos.ganados} ganados · ${conteos.perdidos} perdidos · ${conteos.descartados} descartados`}
          >{conteos.archivados}</strong> archivados
          <span style={{ color: COLOR_GRIS_CL }}> &middot; </span>
          <strong style={{ color: '#dc2626' }}>{conteos.garantias}</strong> garantias
        </div>
      </div>

      {/* PESTANAS — concepto de la LISTA. En el Resumen no aplican: ese siempre enseña el total
          de la empresa, sin importar en que pestaña estuviera el usuario. */}
      {vista === 'tabla' && (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {pestanas.map(p => {
          const activo = pestana === p.id;
          // EXP-ARCHIVO: el contador de "Archivo" es la suma de los tres resultados de cierre.
          const conteo = p.id === 'archivo' ? conteos.archivados : conteos[p.id];
          const tituloPestana = p.id === 'archivo'
            ? `${conteos.ganados} ganados · ${conteos.perdidos} perdidos · ${conteos.descartados} descartados`
            : undefined;
          return (
            <button
              key={p.id}
              onClick={() => setPestana(p.id)}
              title={tituloPestana}
              style={{
                padding: '8px 14px', borderRadius: 99, border: 'none',
                background: activo ? COLOR_BEEP_AZUL : '#fff',
                color: activo ? '#fff' : COLOR_GRIS,
                fontSize: 13, fontWeight: activo ? 700 : 500,
                cursor: 'pointer', fontFamily: FF,
                boxShadow: activo ? '0 2px 6px rgba(24,95,165,0.25)' : '0 1px 2px rgba(0,0,0,0.04)',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {p.label}
              {typeof conteo === 'number' && (
                <span style={{
                  marginLeft: 6, fontSize: 11,
                  opacity: activo ? 0.85 : 0.6,
                }}>({conteo})</span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* ============ LA FILA UNICA DE CONTROLES ============
          Buscador + boton Filtros (con su contador) + accesos a las vistas guardadas + el selector
          de las tres vistas + Nuevo expediente. Antes esto eran TRES filas de filtros siempre
          desplegadas, mas el selector arriba del todo y cuatro tarjetas: media pantalla gastada
          antes de enseñar un solo expediente. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: hayFiltrosAvanzados ? 8 : 16,
      }}>
        {/* Buscador — no en el Resumen (no hay lista que buscar) ni en Actividad/Calendario. */}
        {vista !== 'resumen' && !(pestana === 'actividad' || pestana === 'calendario' || pestana === 'contratistas') && (
          <div style={{ position: 'relative', flex: esCelular ? '1 1 100%' : '1 1 240px', minWidth: 0 }}>
            <span style={{
              position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
              color: COLOR_GRIS_CL, fontSize: 14, pointerEvents: 'none',
            }}>{'\u{1F50D}'}</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={esCelular ? 'Buscar expediente...' : 'Buscar por folio, titulo, cliente, sucursal, ciudad...'}
              style={{
                width: '100%', minHeight: 44, padding: '0 12px 0 36px',
                border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
                fontSize: 14, fontFamily: FF, color: COLOR_OSCURO,
                outline: 'none', boxSizing: 'border-box', background: '#fff',
              }}
            />
          </div>
        )}

        {/* Boton FILTROS, con el contador de cuantos hay puestos. */}
        {vista !== 'resumen' && !(pestana === 'actividad' || pestana === 'calendario' || pestana === 'contratistas') && (
          <button
            ref={botonFiltrosRef}
            type="button"
            onClick={() => setFiltrosAbiertos(v => !v)}
            aria-expanded={filtrosAbiertos}
            style={{
              // MOVIL (360 px): con los respiros de escritorio, Filtros + selector + Nuevo suman
              // 12 px mas de lo que cabe y "+ Nuevo" se cae solo a una tercera fila.
              flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: esCelular ? 5 : 7,
              minHeight: 44, padding: esCelular ? '0 10px' : '0 14px', borderRadius: 10, cursor: 'pointer', fontFamily: FF,
              fontSize: 13.5, fontWeight: 700,
              background: hayFiltrosAvanzados ? '#e8f1fa' : '#fff',
              border: `1px solid ${hayFiltrosAvanzados ? COLOR_BEEP_AZUL : COLOR_BORDE}`,
              color: hayFiltrosAvanzados ? COLOR_BEEP_AZUL : COLOR_GRIS,
            }}
          >
            <SlidersHorizontal size={16} strokeWidth={2.2} />
            Filtros
            {conteoFiltros > 0 && (
              <span style={{
                minWidth: 20, height: 20, borderRadius: 999, background: COLOR_BEEP_AZUL, color: '#fff',
                fontSize: 11.5, fontWeight: 800, display: 'grid', placeItems: 'center', padding: '0 5px',
              }}>{conteoFiltros}</span>
            )}
          </button>
        )}

        {/* Vistas guardadas — hasta tres como boton; las demas viven dentro del panel. */}
        {vista !== 'resumen' && !(pestana === 'actividad' || pestana === 'calendario' || pestana === 'contratistas') &&
          vistasGuardadas.slice(0, MAX_VISTAS_VISIBLES).map(v => {
            const activa = vistaActiva(v, filtrosActuales);
            return (
              <button
                key={v.id} type="button"
                onClick={() => (activa ? limpiarFiltros() : aplicarVista(v))}
                title={activa ? 'Quitar estos filtros' : `Aplicar la vista ${v.nombre}`}
                style={{
                  flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
                  maxWidth: 170, minHeight: 44, padding: '0 12px', borderRadius: 10,
                  cursor: 'pointer', fontFamily: FF, fontSize: 13, fontWeight: 600,
                  background: activa ? COLOR_BEEP_AZUL : '#fff',
                  border: `1px solid ${activa ? COLOR_BEEP_AZUL : COLOR_BORDE}`,
                  color: activa ? '#fff' : COLOR_GRIS,
                }}
              >
                <Bookmark size={13} strokeWidth={2.3} style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.nombre}</span>
              </button>
            );
          })}

        {/* Selector de las TRES vistas. "Resumen" solo existe para quien puede verlo: al resto no
            se le enseña en gris ni bloqueado — simplemente no esta. */}
        {!modoSeleccion && (
          <div style={{
            marginLeft: esCelular ? 0 : 'auto', flexShrink: 0,
            display: 'flex', background: '#fff', borderRadius: 10, padding: 3, border: `1px solid ${COLOR_BORDE}`,
          }}>
            {vistasDisponibles.map(v => (
              <ToggleVista
                key={v.id}
                activo={vista === v.id}
                // EXP-ARCHIVO: el Kanban es un tablero de trabajo EN CURSO; dentro de Archivo no
                // tiene sentido. Se deshabilita en vez de desaparecer, que moveria los botones de
                // sitio cada vez que se cambia de pestaña.
                deshabilitado={v.id === 'kanban' && pestana === 'archivo' && vista !== 'resumen'}
                razonDeshabilitado="El Kanban es de expedientes en curso; en Archivo no aplica."
                onClick={() => cambiarVista(v.id)}
                Icono={v.Icono} label={v.label} esCelular={esCelular}
              />
            ))}
          </div>
        )}

        <button
          onClick={abrirModal}
          style={{
            flexShrink: 0,
            background: COLOR_BEEP_LIMA, color: '#fff', border: 'none',
            borderRadius: 10, padding: esCelular ? '0 12px' : '0 16px',
            fontSize: 14, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(148,184,3,0.25)',
            minHeight: 44, transition: 'background 0.2s',
          }}
        >+ Nuevo{esCelular ? '' : ' expediente'}</button>
      </div>

      {/* ============ LOS FILTROS PUESTOS, COMO ETIQUETAS ============
          Solo existen si hay algo puesto. Sin filtros, esta fila no se dibuja y el bloque entero
          mide la mitad — que es justo lo que pidio Ricardo. */}
      {vista !== 'resumen' && hayFiltrosAvanzados && !(pestana === 'actividad' || pestana === 'calendario' || pestana === 'contratistas') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {chipsActivos.map(c => (
            <ChipActivo key={c.id} label={c.label} color={c.color} onQuitar={c.quitar} />
          ))}
          <button
            onClick={limpiarFiltros}
            style={{
              background: 'transparent', border: 'none', color: COLOR_BEEP_AZUL,
              fontSize: 12.5, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
              minHeight: 44, padding: '0 8px',
            }}
          >Limpiar todo</button>
        </div>
      )}

      {/* EL PANEL DE FILTROS. No se quita NI UNA opcion de las que habia: solo dejan de estar
          todas a la vez en pantalla. */}
      <PanelFiltrosExpedientes
        abierto={filtrosAbiertos}
        onCerrar={() => setFiltrosAbiertos(false)}
        esCelular={esCelular}
        anclaRef={botonFiltrosRef}
        conteoVisible={expedientesVisibles.length}
        hayFiltros={hayFiltrosAvanzados}
        onLimpiar={limpiarFiltros}
        vistas={vistasGuardadas}
        filtrosActuales={filtrosActuales}
        onAplicarVista={aplicarVista}
        onGuardarVista={(nombre) => setVistasGuardadas(guardarVistaFiltros(usuarioId, nombre, filtrosActuales))}
        onBorrarVista={(id) => setVistasGuardadas(borrarVistaFiltros(usuarioId, id))}
        grupos={[
          { id: 'tipo', titulo: 'Tipo de trabajo', contenido: (
            <ChipsFiltroExp
              label="" valor={tipoTrabajo} onChange={setTipoTrabajo}
              opciones={[{ id: '', label: 'Todos' }, ...TIPOS_TRABAJO_FILTRO]}
            />
          ) },
          // EXP-ARCHIVO: "Avance" mide el paso actual de un expediente VIVO; en Archivo no aplica.
          pestana !== 'archivo' && { id: 'avance', titulo: 'Avance', contenido: (
            <ChipsFiltroExp
              label="" valor={semaforoFiltro} onChange={setSemaforoFiltro}
              opciones={[{ id: '', label: 'Todos' }, ...SEMAFORO_FILTRO.map(x => ({ id: x.id, label: x.label, punto: x.color }))]}
            />
          ) },
          pestana === 'activos' && { id: 'sincotizar', titulo: 'Sin cotizar', contenido: (
            <ChipsFiltroExp
              label="" valor={sinCotizarFiltro} onChange={setSinCotizarFiltro}
              opciones={[
                { id: '', label: 'Todos' },
                { id: 'si', label: `Sin cotizar (${expedientes.filter(e => !e.monto_estado).length})` },
                { id: '5', label: '+5 dias', punto: SEMAFORO.amarillo.color },
                { id: '10', label: '+10 dias', punto: SEMAFORO.rojo.color },
              ]}
            />
          ) },
          // EXPEDIENTES-RESUMEN: filtro por estado de la cotizacion. Es el destino de los renglones
          // de dinero del Resumen: "de estos $9.1M en borrador, ¿cuales son?".
          pestana === 'activos' && { id: 'cotizacion', titulo: 'Estado de la cotizacion', contenido: (
            <ChipsFiltroExp
              label="" valor={cotizacionFiltro} onChange={setCotizacionFiltro}
              opciones={[{ id: '', label: 'Todas' }, ...COTIZACION_FILTRO.map(x => ({ id: x.id, label: x.label, punto: x.color }))]}
            />
          ) },
          pestana === 'archivo' && { id: 'resultado', titulo: 'Resultado del cierre', contenido: (
            <ChipsFiltroExp
              label="" valor={resultadoFiltro} onChange={setResultadoFiltro}
              opciones={RESULTADOS_ARCHIVO.map(r => ({
                id: r.id,
                label: `${r.label} (${r.id === '' ? conteos.archivados : conteos[{ ganado: 'ganados', perdido: 'perdidos', descartado: 'descartados' }[r.id]] ?? 0})`,
                punto: r.id ? CIERRE_VISUAL[r.id].color : undefined,
              }))}
            />
          ) },
          pestana === 'activos' && { id: 'estado', titulo: 'Paso del flujo', contenido: (
            <SelectorEstadoFunnel valor={estadoFunnel} onCambio={setEstadoFunnel} />
          ) },
          opcionesResponsable.length > 0 && { id: 'responsable', titulo: 'Responsable', contenido: (
            <DropdownFiltro
              label="Responsable" todosLabel="Todos los responsables"
              valor={responsableId} opciones={opcionesResponsable}
              onCambio={setResponsableId}
            />
          ) },
          opcionesGrupo.length > 0 && { id: 'cliente', titulo: 'Cliente', contenido: (
            <DropdownFiltro
              label="Cliente" todosLabel="Todos los clientes"
              valor={clienteGrupoId} opciones={opcionesGrupo}
              onCambio={setClienteGrupoId}
            />
          ) },
          opcionesFamilia.length > 0 && { id: 'rubro', titulo: 'Rubro', contenido: (
            <DropdownFiltro
              label="Rubro" todosLabel="Todos los rubros"
              valor={familiaFiltro} opciones={opcionesFamilia}
              onCambio={setFamiliaFiltro}
            />
          ) },
          opcionesNecesidad.length > 0 && { id: 'necesidad', titulo: 'Necesidad', contenido: (
            <DropdownFiltro
              label="Necesidad" todosLabel="Todas las necesidades"
              valor={necesidadFiltro} opciones={opcionesNecesidad}
              onCambio={setNecesidadFiltro}
            />
          ) },
          { id: 'fechas', titulo: 'Fechas', contenido: (
            <FiltroFecha
              desde={fechaDesde} hasta={fechaHasta}
              onDesde={setFechaDesde} onHasta={setFechaHasta}
            />
          ) },
        ].filter(Boolean)}
      />

      {/* GARANTIAS (Entrega 2): torre de control de garantias por vencer, arriba de la lista. */}
      {vista !== 'resumen' && pestana === 'garantias' && (
        <div style={{ marginBottom: 14 }}>
          <GarantiasAvisosPanel esCelular={esCelular} onAbrirExpediente={(id) => setSeleccionId(id)} />
        </div>
      )}
      {vista === 'resumen' ? (
        /* ===== RESUMEN DE LA EMPRESA (EXPEDIENTES-RESUMEN FRENTE B) =====
           Siempre el total de la empresa. Cada renglon lleva a la lista ya filtrada. */
        <ResumenEmpresa
          datos={resumen}
          cargando={resumenCargando && !resumen}
          error={resumenError}
          onReintentar={cargarResumen}
          onIrALista={irALista}
          esCelular={esCelular}
        />
      ) : vista === 'tabla' && pestana === 'calendario' ? (
        /* CALENDARIO-E2: agenda por tecnico (el corazon del calendario de campo) */
        <CalendarioCampo esCelular={esCelular} />
      ) : vista === 'tabla' && pestana === 'contratistas' ? (
        /* CONTRATISTAS-EXTERNOS Bloque 1: cuadrillas externas, su gente y sus documentos */
        <Contratistas esCelular={esCelular} />
      ) : vista === 'tabla' && pestana === 'actividad' ? (
        /* ACTIVIDAD-GLOBAL: timeline de todo lo que pasa con expedientes */
        <ActividadExpedientes usuarios={usuarios} onAbrirExpediente={(id) => setSeleccionId(id)} />
      ) : vista === 'kanban' ? (
        /* ===== KANBAN (secundaria) — reusa VistaKanban del panel global ===== */
        panelError ? (
          /* EXPEDIENTES-RESUMEN: antes, un 403 del panel (rol tecnico, sin acceso financiero)
             dejaba esto en "Cargando tablero..." PARA SIEMPRE. Ahora se dice lo que pasa. */
          <div style={{
            background: '#fff', border: `1px dashed ${COLOR_BORDE}`, borderRadius: 12,
            padding: '38px 20px', textAlign: 'center',
          }}>
            <div style={{ color: COLOR_OSCURO, fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
              No se puede mostrar el tablero
            </div>
            <div style={{ color: COLOR_GRIS, fontSize: 13, marginBottom: 18, lineHeight: 1.5 }}>{panelError}</div>
            <button
              onClick={() => cambiarVista('tabla')}
              style={{
                background: COLOR_BEEP_AZUL, color: '#fff', border: 'none', borderRadius: 10,
                padding: '0 18px', minHeight: 44, fontSize: 13.5, fontWeight: 700,
                fontFamily: FF, cursor: 'pointer',
              }}
            >Ver la lista</button>
          </div>
        ) : !panel ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CL, fontSize: 13 }}>
            Cargando tablero...
          </div>
        ) : columnasKanban.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CL, fontSize: 13 }}>
            No hay columnas para mostrar en este tipo de trabajo.
          </div>
        ) : (
          <VistaKanban
            columnas={columnasKanban}
            expedientes={expedientesKanban}
            claveColumna={claveColumnaKanban}
            onAbrir={(id) => setSeleccionId(id)}
            onAvisar={setAvisarExp}
            esCelular={esCelular}
            onIrTabla={() => cambiarVista('tabla')}
          />
        )
      ) : (
      <>
      {/* LISTA / ESTADOS (vista Tabla) */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CL, fontSize: 13 }}>
          Cargando expedientes...
        </div>
      )}

      {!loading && errorLista && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          padding: 16, borderRadius: 10, fontSize: 13,
        }}>
          No se pudo cargar la lista: {errorLista}
        </div>
      )}

      {!loading && !errorLista && expedientesVisibles.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: '#fff', borderRadius: 12, border: `1px dashed ${COLOR_BORDE}`,
        }}>
          <div style={{ fontSize: 56, marginBottom: 12, opacity: 0.4 }}>{'\u{1F4C1}'}</div>
          <div style={{ color: COLOR_OSCURO, fontWeight: 700, fontSize: 18, marginBottom: 6 }}>
            {(searchDeb || hayFiltrosAvanzados)
              ? 'Ningun expediente coincide con esos filtros'
              : `No hay expedientes en la pestana ${PESTANAS.find(p => p.id === pestana)?.label || pestana}`}
          </div>
          <div style={{ color: COLOR_GRIS_CL, fontSize: 13, marginBottom: 24 }}>
            {(searchDeb || hayFiltrosAvanzados)
              ? 'Proba ajustar la busqueda o limpiar los filtros'
              : (pestana === 'activos' ? 'Crea el primero capturando un nuevo interes' : 'Cambia de pestana para ver otros estados')}
          </div>
          {!(searchDeb || hayFiltrosAvanzados) && pestana === 'activos' && (
            <button
              onClick={abrirModal}
              style={{
                background: COLOR_BEEP_LIMA, color: '#fff', border: 'none',
                borderRadius: 10, padding: '12px 22px',
                fontSize: 14, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
                minHeight: 44,
              }}
            >+ Nuevo expediente</button>
          )}
        </div>
      )}

      {!loading && !errorLista && expedientesVisibles.length > 0 && (
        compacto ? (
          // EXP-LISTA-V2: vista tarjeta-fila (movil, tablet y PC vertical <1100).
          // No scroll horizontal; el renglon envuelve limpio.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {padres.flatMap(padre => [
              <TarjetaFilaExpediente
                key={padre.id}
                expediente={padre}
                esAnexo={false}
                esArchivo={esArchivo}
                onClick={() => onClickCard(padre)}
                catEstados={catEstados}
                usuarios={usuarios}
                onActualizar={actualizarExpedienteInline}
                umbrales={panel?.umbrales}
              />,
              ...((anexosPorPadre[padre.id] || []).map(anexo => (
                <TarjetaFilaExpediente
                  key={anexo.id}
                  expediente={anexo}
                  esAnexo={true}
                  esArchivo={esArchivo}
                  onClick={() => onClickCard(anexo)}
                  catEstados={catEstados}
                  usuarios={usuarios}
                  onActualizar={actualizarExpedienteInline}
                  umbrales={panel?.umbrales}
                />
              ))),
            ])}
          </div>
        ) : (
          // TRANSPARENCIA-TOTAL: tabla unificada. Columnas: Titulo > Cliente > Folio >
          // Tipo de trabajo > Paso actual > Responsable > Dias en paso (semaforo) >
          // Monto > Fecha > Ver. Encabezados ordenables (ordenan los PADRES).
          /* EXPEDIENTES-RESUMEN FRENTE A (2026-09-17) — EL ARREGLO DEL SCROLL HORIZONTAL.
             ANTES: <div overflowX:'auto'> SIN alto acotado. Con 168 filas la caja media 12,722 px
             y su borde inferior caia en y=13,379, con una ventana de 688 px: la barra horizontal
             existia, pero quedaba 12,691 px por debajo de la pantalla. Las columnas Monto, Fecha y
             "Ver expediente" no se podian alcanzar de ninguna forma.
             AHORA: ScrollportTabla acota el alto a lo que queda de ventana, asi que la barra esta
             SIEMPRE a la vista, con sombra en el borde, arrastre con el raton y Shift+rueda.
             Es el MISMO arreglo que ya se hizo en el Kanban el 2026-09-10 y que aqui nadie toco;
             por eso el sprint amplia tambien el portero para que no haya una tercera vez. */
          <ScrollportTabla
            estilo={{
              background: '#fff', borderRadius: 10, border: `1px solid ${COLOR_BORDE}`,
            }}
          >
            <table style={{
              width: '100%', minWidth: 1100, borderCollapse: 'collapse', fontSize: 13, fontFamily: FF,
            }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <ThOrden campo="titulo"      label="Titulo"        orden={orden} onOrden={toggleOrden} />
                  <th style={celdaHeader}>Cliente</th>
                  <ThOrden campo="folio"       label="Folio"         orden={orden} onOrden={toggleOrden} />
                  <ThOrden campo="tipo"        label="Tipo"          orden={orden} onOrden={toggleOrden} />
                  {/* EXP-ARCHIVO: en Archivo, "Paso actual" y "Dias en paso" no dicen nada (el
                      expediente ya no avanza). Se cambian por Resultado y "Cerrado el". */}
                  {esArchivo
                    ? <ThOrden campo="resultado" label="Resultado"   orden={orden} onOrden={toggleOrden} />
                    : <ThOrden campo="paso"      label="Paso actual" orden={orden} onOrden={toggleOrden} />}
                  <ThOrden campo="responsable" label="Responsable"   orden={orden} onOrden={toggleOrden} />
                  {/* CREADO-POR (2026-09-24): el responsable lo asigna el motor de flujos
                      por AREA, asi que casi nunca es quien dio de alta el expediente —
                      203 de 315 difieren. Sin esta columna no habia forma de saber de
                      quien era el trabajo. El backend ya mandaba `creado_por_nombre`. */}
                  <ThOrden campo="creador"     label="Creado por"    orden={orden} onOrden={toggleOrden} />
                  {esArchivo
                    ? <ThOrden campo="cerrado"  label="Cerrado el"   orden={orden} onOrden={toggleOrden} />
                    : <ThOrden campo="dias"     label="Dias en paso" orden={orden} onOrden={toggleOrden} alineado="right" />}
                  <ThOrden campo="monto"       label="Monto"         orden={orden} onOrden={toggleOrden} alineado="right" />
                  <ThOrden campo="fecha"       label="Fecha"         orden={orden} onOrden={toggleOrden} />
                  <th style={{ ...celdaHeader, textAlign: 'right' }} aria-label="Acciones"></th>
                </tr>
              </thead>
              <tbody>
                {padres.flatMap(padre => [
                  <FilaExpediente
                    key={padre.id}
                    expediente={padre}
                    esAnexo={false}
                    esArchivo={esArchivo}
                    onClick={() => onClickCard(padre)}
                    catEstados={catEstados}
                    usuarios={usuarios}
                    onActualizar={actualizarExpedienteInline}
                    umbrales={panel?.umbrales}
                  />,
                  ...((anexosPorPadre[padre.id] || []).map(anexo => (
                    <FilaExpediente
                      key={anexo.id}
                      expediente={anexo}
                      esAnexo={true}
                      esArchivo={esArchivo}
                      onClick={() => onClickCard(anexo)}
                      catEstados={catEstados}
                      usuarios={usuarios}
                      onActualizar={actualizarExpedienteInline}
                      umbrales={panel?.umbrales}
                    />
                  ))),
                ])}
              </tbody>
            </table>
          </ScrollportTabla>
        )
      )}
      </>
      )}

      {/* MODAL */}
      {modalAbierto && (
        <ModalNuevoExpediente
          form={form} setForm={setForm} setCampo={setCampo}
          guardando={guardando}
          errorForm={errorForm}
          formularioValido={formularioValido}
          onCerrar={cerrarModal}
          onGuardar={guardar}
          esCelular={esCelular}
        />
      )}

      {/* DOS-TIEMPOS: prompt post-creacion — ofrecer contexto SIN bloquear */}
      {contextoPrompt && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 4100,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: FF,
        }}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420,
            padding: 24, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', textAlign: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%', background: '#eefaf1',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
            }}>
              <CheckCircle2 size={28} color="#16a34a" />
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: COLOR_OSCURO }}>
              Expediente {contextoPrompt.folio} creado
            </div>
            <div style={{ fontSize: 13, color: COLOR_GRIS, marginTop: 6, lineHeight: 1.5 }}>
              Ya existe y entro a su flujo. ¿Quieres agregar contexto ahora
              (como llego, que necesita)? Tambien puedes hacerlo despues desde el expediente.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setContextoPrompt(null)}
                style={{
                  background: '#fff', color: COLOR_GRIS, border: `1px solid ${COLOR_BORDE}`,
                  borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 600,
                  fontFamily: FF, cursor: 'pointer', minHeight: 44,
                }}
              >Despues</button>
              <button
                onClick={() => { setSeleccionId(contextoPrompt.id); setContextoPrompt(null); }}
                style={{
                  background: COLOR_BEEP_AZUL, color: '#fff', border: 'none',
                  borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 700,
                  fontFamily: FF, cursor: 'pointer', minHeight: 44,
                }}
              >Abrir y agregar contexto</button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: toast.tipo === 'ok' ? COLOR_BEEP_LIMA : '#dc2626',
          color: '#fff', padding: '12px 22px', borderRadius: 10,
          fontSize: 14, fontWeight: 600, fontFamily: FF, zIndex: 5000,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}>{toast.msg}</div>
      )}

      {/* Avisar al responsable (desde el Kanban, expedientes atorados) */}
      {avisarExp && (
        <ModalAvisarResponsable
          exp={avisarExp}
          onClose={() => setAvisarExp(null)}
          onEnviado={(msg) => mostrarToast(msg || 'Recordatorio registrado.')}
        />
      )}
    </div>
  );
}

// ============================================
// SUBCOMPONENTES DE LA VISTA UNIFICADA (TRANSPARENCIA-TOTAL)
// ============================================

// Boton del selector de vista (Resumen / Tabla / Kanban).
// EXPEDIENTES-RESUMEN (2026-09-17): admite quedarse DESHABILITADO (el Kanban dentro de Archivo).
// Se deshabilita en vez de desaparecer porque un boton que se esfuma mueve de sitio a los demas
// cada vez que se cambia de pestaña, y se acaba pulsando el que no era.
function ToggleVista({ activo, onClick, Icono, label, esCelular, deshabilitado, razonDeshabilitado }) {
  return (
    <button
      onClick={deshabilitado ? undefined : onClick}
      disabled={deshabilitado}
      title={deshabilitado ? razonDeshabilitado : label}
      aria-pressed={activo}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: esCelular ? '8px 8px' : '8px 14px',
        borderRadius: 8, border: 'none', fontFamily: FF, fontSize: 13, fontWeight: 600, minHeight: 44,
        background: activo ? COLOR_BEEP_AZUL : 'transparent',
        color: activo ? '#fff' : COLOR_GRIS,
        boxShadow: activo ? '0 1px 3px rgba(24,95,165,0.25)' : 'none',
        opacity: deshabilitado ? 0.38 : 1,
        cursor: deshabilitado ? 'not-allowed' : 'pointer', transition: 'background 0.15s, color 0.15s',
      }}
    >
      <Icono size={16} />
      {!esCelular && label}
    </button>
  );
}

// Chips de filtro (Tipo de trabajo / Avance). Etiqueta + botones tipo pastilla.
function ChipsFiltroExp({ label, valor, onChange, opciones }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      {/* Dentro del panel de filtros el titulo del grupo ya lo pone el propio panel, asi que se
          pasa label="" y aqui no se dibuja nada (si no, saldria un ":" suelto). */}
      {label ? <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>{label}:</span> : null}
      {opciones.map(o => {
        const activo = valor === o.id;
        return (
          <button
            key={o.id || 'todos'}
            onClick={() => onChange(o.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 999,
              border: `1px solid ${activo ? COLOR_BEEP_AZUL : COLOR_BORDE}`,
              background: activo ? COLOR_BEEP_AZUL : '#fff',
              color: activo ? '#fff' : COLOR_GRIS, fontFamily: FF, fontSize: 12, fontWeight: 600,
              cursor: 'pointer', minHeight: 44,
            }}
          >
            {o.punto && <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.punto }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Encabezado de columna ordenable (ordena los PADRES de la tabla).
function ThOrden({ campo, label, orden, onOrden, alineado = 'left' }) {
  const activo = orden.campo === campo;
  return (
    <th
      onClick={() => onOrden(campo)}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOrden(campo); } }}
      aria-sort={activo ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      style={{ ...celdaHeader, textAlign: alineado, cursor: 'pointer', color: activo ? COLOR_BEEP_AZUL : celdaHeader.color }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: alineado === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        <ArrowUpDown size={12} style={{ opacity: activo ? 1 : 0.35 }} />
      </span>
    </th>
  );
}

// Chip del tipo de trabajo (workflow). Reusa la paleta compartida TIPO_VISUAL.
function TipoTrabajoChip({ expediente }) {
  const t = TIPO_VISUAL[expediente.workflow_codigo];
  if (!t) return <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color: t.color, background: t.bg,
      padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap',
    }}>{t.label}</span>
  );
}

// EXP-ARCHIVO: chip del RESULTADO del cierre (Ganado verde / Perdido rojo / Descartado gris).
// En perdidos y descartados el MOTIVO firmado del cierre viaja en el tooltip; completo va en la
// ficha. Los colores salen de CIERRE_VISUAL (paleta del modulo), nunca sueltos.
function textoMotivoCierre(exp) {
  const partes = [exp.razon_cierre, exp.nota_cierre].map(x => (x || '').trim()).filter(Boolean);
  return partes.length ? partes.join(' — ') : '';
}
function ChipResultado({ expediente, compacto = false }) {
  const v = CIERRE_VISUAL[expediente.tipo_cierre];
  if (!v) return <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>;
  const motivo = textoMotivoCierre(expediente);
  const fecha = fechaCorta(expediente.fecha_cierre || expediente.cerrado_at);
  const titulo = [v.label, fecha ? `cerrado el ${fecha}` : '', motivo].filter(Boolean).join(' · ');
  return (
    <span
      title={titulo}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontSize: compacto ? 10.5 : 11.5, fontWeight: 700, color: v.color,
        background: `${v.color}14`, border: `1px solid ${v.color}40`,
        padding: compacto ? '2px 7px' : '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
        cursor: motivo ? 'help' : 'default',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: v.color, flexShrink: 0 }} />
      {v.label}
    </span>
  );
}

// Chip de dias en el paso con semaforo (verde/amarillo/rojo). Solo tiene sentido
// en expedientes activos con paso; en cerrados muestra un guion neutro.
function DiasSemaforoChip({ expediente, umbrales }) {
  if (expediente.tipo_cierre || expediente.dias_en_paso == null) {
    return <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>;
  }
  const sem = SEMAFORO[semaforoDe(expediente.dias_en_paso, umbrales)] || SEMAFORO.verde;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600,
      color: sem.color, background: sem.bg, border: `1px solid ${sem.borde}`,
      padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      <Clock size={11} /> {textoDias(expediente.dias_en_paso)}
    </span>
  );
}

// Celda de monto: cifra con formato + marca discreta si aun no esta autorizada.
// MOVIL-2 (2026-09-14): misma regla de lectura que en el Kanban (fuente unica `lecturaMonto`):
// autorizado = negro, sin autorizar = gris claro (dinero probable), sin cotizacion = "Sin cotizar ·
// N dias" contando desde el ALTA, con el color de alerta del semaforo. Nunca "sin monto".
function MontoCelda({ expediente }) {
  const d = lecturaMonto(expediente);
  // EXPEDIENTES-RESUMEN (2026-09-17): si el monto es de otra persona, el hueco se queda VACIO.
  // Ni "$0" (seria mentira) ni un candado (llamaria la atencion sobre algo que no se puede abrir).
  // El dato no viene siquiera del servidor: aqui solo se decide que no se dibuje nada.
  if (d.estado === 'oculto') return null;
  if (d.estado === 'sin_cotizar') {
    return (
      <span title={d.titulo} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700,
        color: d.color, background: d.bg, border: `1px solid ${d.borde}`,
        padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
      }}>{d.texto}</span>
    );
  }
  return (
    <span title={d.titulo} style={{ fontSize: 13, fontWeight: 700, color: d.color, whiteSpace: 'nowrap' }}>
      {d.texto}
    </span>
  );
}

// ============================================
// AUTH HEADER — el resto de la app suele guardar el token en
// localStorage.beep_token. Se incluye solo si existe; sin token,
// /api/usuarios devolvera 401 y simplemente no se cargan responsables.
// ============================================
function authHeaders() {
  try {
    const t = localStorage.getItem('beep_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
}

// ============================================
// SELECTOR DE ESTADO DEL FUNNEL (solo pestana Activos)
// ============================================
// Dropdown custom premium: <button> que abre un panel flotante con los 19
// estados, cada uno con circulito de color (no fondo lleno como el <select>
// nativo, que se veia saturado). Imita el patron de BadgeEstadoEditable:
//   - Portal a document.body (anti-overflow)
//   - position: fixed con flip automatico si no cabe abajo
//   - Click-outside chequeando ambos refs (trigger + dropdown estan en
//     arboles del DOM distintos por usar createPortal)
//   - Escape para cerrar
function SelectorEstadoFunnel({ valor, onCambio }) {
  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e) => {
      const enTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const enDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!enTrigger && !enDropdown) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  const abrirCerrar = (e) => {
    e.stopPropagation();
    if (abierto) { setAbierto(false); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const dropdownAlto = 320;
      const espacioAbajo = window.innerHeight - rect.bottom;
      const arribaSiNoCabe = espacioAbajo < dropdownAlto && rect.top > dropdownAlto;
      setPosicion({
        top: arribaSiNoCabe ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 220),
        transform: arribaSiNoCabe ? 'translateY(-100%)' : 'none',
      });
    }
    setAbierto(true);
  };

  const elegir = (e, codigo) => {
    e.stopPropagation();
    setAbierto(false);
    onCambio(codigo);
  };

  const meta = valor ? ESTADO_VISUAL_OPORTUNIDAD[valor] : null;
  const todosSel = valor == null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600 }}>Estado:</span>
      <button
        ref={triggerRef}
        type="button"
        onClick={abrirCerrar}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          padding: '6px 10px',
          border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
          fontSize: 12, fontFamily: FF,
          background: '#fff', color: COLOR_OSCURO,
          cursor: 'pointer', minWidth: 180,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {meta && (
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: meta.color, flexShrink: 0,
            }} />
          )}
          <span>{meta ? meta.label : 'Todos los estados'}</span>
        </span>
        <span style={{ fontSize: 10, color: COLOR_GRIS }}>{abierto ? '▴' : '▾'}</span>
      </button>
      {abierto && posicion && createPortal(
        <div
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: posicion.top,
            left: posicion.left,
            width: posicion.width,
            transform: posicion.transform,
            maxHeight: 320,
            overflowY: 'auto',
            background: '#fff',
            border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
            boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
            zIndex: 9999,
            padding: 4,
            fontFamily: FF, fontSize: 12,
          }}
        >
          <div
            onClick={(e) => elegir(e, null)}
            onMouseEnter={(e) => { if (!todosSel) e.currentTarget.style.background = '#f9fafb'; }}
            onMouseLeave={(e) => { if (!todosSel) e.currentTarget.style.background = '#fff'; }}
            style={{
              padding: '6px 10px', cursor: 'pointer', borderRadius: 4,
              background: todosSel ? '#f3f4f6' : '#fff',
              fontWeight: todosSel ? 600 : 400,
              color: COLOR_OSCURO,
              borderBottom: `1px solid ${COLOR_BORDE}`,
              marginBottom: 2,
            }}
          >Todos los estados</div>
          {ESTADOS_VALIDOS_FUNNEL.map(codigo => {
            const m = ESTADO_VISUAL_OPORTUNIDAD[codigo];
            if (!m) return null;
            const sel = valor === codigo;
            return (
              <div
                key={codigo}
                onClick={(e) => elegir(e, codigo)}
                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = '#fff'; }}
                style={{
                  padding: '6px 10px', cursor: 'pointer', borderRadius: 4,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: sel ? '#f3f4f6' : '#fff',
                  fontWeight: sel ? 600 : 400,
                  color: COLOR_OSCURO,
                }}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: m.color, flexShrink: 0,
                }} />
                <span>{m.label}</span>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================
// DROPDOWN GENERICO
// ============================================
function DropdownFiltro({ label, todosLabel, valor, opciones, onCambio }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLOR_GRIS }}>
      <span style={{ fontWeight: 600 }}>{label}:</span>
      <select
        value={valor == null ? '' : String(valor)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') onCambio(null);
          else {
            const opt = opciones.find(o => String(o.value) === v);
            onCambio(opt ? opt.value : v);
          }
        }}
        style={{
          padding: '6px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
          fontSize: 12, fontFamily: FF, background: '#fff', color: COLOR_OSCURO,
          cursor: 'pointer', minWidth: 160,
        }}
      >
        <option value="">{todosLabel}</option>
        {opciones.map(o => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

// ============================================
// FILTRO POR RANGO DE FECHAS (sobre fecha_interes)
// ============================================
function FiltroFecha({ desde, hasta, onDesde, onHasta }) {
  // minHeight 44 = area tactil minima movil; fontSize 16 evita el auto-zoom
  // de Safari iOS al enfocar el input.
  const estiloFecha = {
    padding: '8px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
    fontSize: 16, fontFamily: FF, background: '#fff',
    color: COLOR_OSCURO, cursor: 'pointer', minWidth: 140, minHeight: 44,
    boxSizing: 'border-box', transition: 'border-color 0.15s',
    WebkitTapHighlightColor: 'transparent',
  };
  const focus  = (e) => { e.target.style.borderColor = COLOR_BEEP_AZUL; };
  const blur   = (e) => { e.target.style.borderColor = COLOR_BORDE; };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLOR_GRIS, flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600 }}>Fecha:</span>
      <input
        type="date"
        value={desde}
        max={hasta || undefined}
        onChange={(e) => onDesde(e.target.value)}
        onFocus={focus} onBlur={blur}
        aria-label="Fecha desde"
        style={estiloFecha}
      />
      <span style={{ color: COLOR_GRIS_CL }}>{'–'}</span>
      <input
        type="date"
        value={hasta}
        min={desde || undefined}
        onChange={(e) => onHasta(e.target.value)}
        onFocus={focus} onBlur={blur}
        aria-label="Fecha hasta"
        style={estiloFecha}
      />
    </div>
  );
}

// EXPEDIENTES-RESUMEN (2026-09-17): aqui vivia `ResumenFiltros`, que solo enseñaba los CINCO
// filtros "avanzados" (estado, responsable, cliente, necesidad, fechas) y dejaba fuera los chips de
// Tipo / Avance / Sin cotizar / Resultado. Con los filtros ya PLEGADOS eso seria un agujero: un
// filtro puesto que no aparece en ninguna etiqueta no se ve por ningun lado. Lo sustituye
// `chipsActivos`, arriba, que cubre los diez.

// ============================================
// EXP-LISTA: estilos y componente de fila de la tabla jerarquica
// ============================================
const celdaHeader = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  borderBottom: '1px solid #e5e7eb',
  whiteSpace: 'nowrap',
};

const celdaCuerpo = {
  // EXP-UX frente 2: renglones mas gruesos (14px vertical vs 10px antes).
  padding: '14px 12px',
  borderBottom: '1px solid #f3f4f6',
  verticalAlign: 'middle',
};

function fechaCorta(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'short', year: '2-digit',
  });
}

// ============================================
// EXP-UX FRENTE 1: cliente SIEMPRE visible (cadena de respaldo + ubicacion)
// ============================================
// Antes la lista solo mostraba cliente_grupo_nombre -> personas fisicas y
// razones sociales salian sin cliente. Ahora resolvemos en orden:
// (a) sucursal (nombre + grupo), (b) razon social, (c) grupo, (d) persona
// fisica (nombre + direccion de respaldo), (e) contacto libre de leads viejos.
// Todos los campos ya vienen del backend (oportunidades.js GET), no hace falta
// tocar la consulta.
function inicialesCliente(nombre) {
  const partes = String(nombre || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  return (partes[0][0] + (partes[1] ? partes[1][0] : '')).toUpperCase();
}

function resolverClienteExpediente(exp) {
  const grupo    = exp.cliente_grupo_nombre;
  const sucursal = exp.cliente_sucursal_nombre;
  const razon    = exp.cliente_razon_social_nombre;
  const persona  = exp.cliente_persona_nombre;
  const dirSuc = [exp.cliente_sucursal_direccion, exp.cliente_sucursal_ciudad].filter(Boolean).join(', ');
  const dirPer = [exp.cliente_persona_direccion, exp.cliente_persona_ciudad].filter(Boolean).join(', ');

  // EXP-UBICACION: para clientes SIN sucursal, la ubicacion de respaldo es (en
  // orden): la ubicacion libre capturada (tipo + especificacion), luego la
  // direccion de la persona, luego "Sin ubicacion registrada".
  const ubicLibre = textoUbicacionLibre(exp);
  const ubicPersona = ubicLibre || dirPer || 'Sin ubicacion registrada';
  const faltaUbicPersona = !ubicLibre && !dirPer;

  if (sucursal) return { nombre: sucursal, sub: grupo || dirSuc || null, esPersona: false };
  if (razon)    return { nombre: razon, sub: grupo || ubicLibre || null, esPersona: false };
  if (grupo)    return { nombre: grupo, sub: null, esPersona: false };
  // Persona fisica: nombre + ubicacion de respaldo.
  if (persona)  return { nombre: persona, sub: ubicPersona, esPersona: true, faltaUbic: faltaUbicPersona };
  // Respaldo final: leads viejos sin vinculo estructurado (solo contacto libre).
  if (exp.contacto_empresa) return { nombre: exp.contacto_empresa, sub: exp.contacto_nombre || ubicLibre || null, esPersona: false };
  if (exp.contacto_nombre)  return { nombre: exp.contacto_nombre, sub: ubicPersona, esPersona: true, faltaUbic: faltaUbicPersona };
  return { nombre: 'Sin cliente', sub: ubicPersona, esPersona: false, faltaUbic: faltaUbicPersona };
}

// Celda/bloque de cliente: icono (edificio o iniciales) + nombre (peso 500) +
// subtitulo (grupo o ubicacion). `compacto` reduce para la tabla desktop.
function ClienteExpedienteCelda({ expediente, compacto }) {
  const c = resolverClienteExpediente(expediente);
  const avatar = c.esPersona
    ? (
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: '#e8eef7', color: COLOR_BEEP_AZUL,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{inicialesCliente(c.nombre)}</div>
    )
    : (
      <div style={{
        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
        background: '#eef2f7', color: COLOR_BEEP_AZUL,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Building2 size={17} strokeWidth={2} /></div>
    );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
      {avatar}
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 500, color: COLOR_OSCURO, lineHeight: 1.25,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: compacto ? 'nowrap' : 'normal',
        }}>{c.nombre}</div>
        {c.sub && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4, marginTop: 1,
            fontSize: 12, color: c.faltaUbic ? COLOR_GRIS_CL : COLOR_GRIS, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: compacto ? 'nowrap' : 'normal',
          }}>
            {c.esPersona && <MapPin size={11} strokeWidth={2} style={{ flexShrink: 0, opacity: c.faltaUbic ? 0.6 : 1 }} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.sub}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FilaExpediente({ expediente, esAnexo, onClick, catEstados, usuarios, onActualizar, umbrales, esArchivo = false }) {
  const [hover, setHover] = useState(false);
  const fechaTxt = fechaCorta(expediente.fecha_interes);

  return (
    <tr
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: 'pointer',
        background: hover ? '#f5f5f5' : '#fff',
        transition: 'background 0.12s',
      }}
    >
      {/* TITULO (editable) */}
      <td style={{ ...celdaCuerpo, paddingLeft: esAnexo ? 32 : 12 }}>
        {esAnexo && <span style={{ color: COLOR_GRIS_CL, marginRight: 6 }}>{'└─'} </span>}
        <TituloEditable
          expediente={expediente}
          onActualizar={onActualizar}
        />
      </td>
      {/* CLIENTE — cadena de respaldo (grupo/sucursal/ubicacion) + icono */}
      <td style={{ ...celdaCuerpo, maxWidth: 260 }}>
        <ClienteExpedienteCelda expediente={expediente} compacto />
      </td>
      {/* FOLIO — icono Lucide en vez de emoja. CREADO-POR (2026-07-11): el
          creador viaja en el tooltip del folio (criterio de espacio: la tabla
          ya va apretada; el dato completo vive en la ficha). */}
      <td style={{ ...celdaCuerpo, whiteSpace: 'nowrap' }}
          title={expediente.creado_por_nombre ? `Creado por ${expediente.creado_por_nombre}` : undefined}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: COLOR_GRIS }}>
          {esAnexo
            ? <Paperclip size={14} strokeWidth={2} />
            : <Folder size={14} strokeWidth={2} />}
          <strong style={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
            fontSize: 12, color: COLOR_OSCURO,
          }}>{expediente.folio}</strong>
        </span>
      </td>
      {/* TIPO DE TRABAJO (workflow) */}
      <td style={celdaCuerpo}>
        <TipoTrabajoChip expediente={expediente} />
      </td>
      {/* PASO ACTUAL (editable) — en Archivo se cambia por el RESULTADO del cierre */}
      <td style={celdaCuerpo}>
        {esArchivo
          ? <ChipResultado expediente={expediente} />
          : <BadgeEstadoEditable
              expediente={expediente}
              catEstados={catEstados}
              onActualizar={onActualizar}
            />}
      </td>
      {/* RESPONSABLE (editable) */}
      <td style={celdaCuerpo}>
        <ResponsableEditable
          expediente={expediente}
          usuarios={usuarios}
          onActualizar={onActualizar}
        />
      </td>
      {/* CREADO POR — quien dio de alta el expediente. Solo lectura: el creador no
          se cambia nunca, es un hecho historico. */}
      <td style={{ ...celdaCuerpo, fontSize: 12.5, color: COLOR_GRIS, whiteSpace: 'nowrap' }}>
        {expediente.creado_por_nombre || <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>}
      </td>
      {/* DIAS EN PASO (semaforo) — en Archivo se cambia por la FECHA DE CIERRE */}
      {esArchivo ? (
        <td style={{ ...celdaCuerpo, fontSize: 12, color: COLOR_GRIS, whiteSpace: 'nowrap' }}>
          {fechaCorta(expediente.fecha_cierre || expediente.cerrado_at) || <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>}
        </td>
      ) : (
        <td style={{ ...celdaCuerpo, textAlign: 'right' }}>
          <DiasSemaforoChip expediente={expediente} umbrales={umbrales} />
        </td>
      )}
      {/* MONTO */}
      <td style={{ ...celdaCuerpo, textAlign: 'right' }}>
        <MontoCelda expediente={expediente} />
      </td>
      {/* FECHA */}
      <td style={{ ...celdaCuerpo, fontSize: 12, color: COLOR_GRIS, whiteSpace: 'nowrap' }}>
        {fechaTxt || <span style={{ color: COLOR_GRIS_CL }}>{'—'}</span>}
      </td>
      {/* ACCION — Ver expediente (frente 2). La fila entera ya abre; este boton
          da la affordance explicita con texto. */}
      <td style={{ ...celdaCuerpo, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClick(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: hover ? COLOR_BEEP_AZUL : '#fff',
            color: hover ? '#fff' : COLOR_BEEP_AZUL,
            border: `1.5px solid ${COLOR_BEEP_AZUL}55`, borderRadius: 8,
            padding: '0 12px', minHeight: 44, fontSize: 13, fontWeight: 600,
            fontFamily: FF, cursor: 'pointer', transition: 'background 0.15s, color 0.15s',
          }}
        >
          Ver expediente
          <ChevronRight size={15} strokeWidth={2.5} />
        </button>
      </td>
    </tr>
  );
}

// ============================================
// EXP-LISTA-V2: vista tarjeta-fila para mobile
// ============================================
function TarjetaFilaExpediente({ expediente, esAnexo, onClick, catEstados, usuarios, onActualizar, umbrales, esArchivo = false }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 10,
        // Frente 2: renglon mas grueso (14/16 vs 12).
        padding: '16px 14px', paddingLeft: esAnexo ? 28 : 14,
        cursor: 'pointer', fontFamily: FF,
        display: 'flex', flexDirection: 'column', gap: 9,
      }}
    >
      {/* Linea 1: estado (editable) + folio chico derecha con icono. flexWrap:
          los nombres de paso largos (ej. "Ejecucion del servicio") envuelven en
          celular angosto en vez de desbordar (endurecimiento Dona Frida 2026-07-09). */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {/* EXP-ARCHIVO: en Archivo el paso ya no cambia; manda el RESULTADO del cierre. */}
        {esArchivo ? (
          <ChipResultado expediente={expediente} />
        ) : (
          <BadgeEstadoEditable
            expediente={expediente}
            catEstados={catEstados}
            onActualizar={onActualizar}
          />
        )}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          fontSize: 11, color: COLOR_GRIS_CL, flexShrink: 0,
        }}>
          {esAnexo ? <Paperclip size={12} strokeWidth={2} /> : <Folder size={12} strokeWidth={2} />}
          {expediente.folio}
        </span>
      </div>
      {/* Linea 2: titulo (editable) */}
      <div style={{ fontSize: 15, fontWeight: 700, color: COLOR_OSCURO, lineHeight: 1.3 }}>
        <TituloEditable
          expediente={expediente}
          onActualizar={onActualizar}
        />
      </div>
      {/* Linea 3: cliente SIEMPRE visible con icono + ubicacion (frente 1) */}
      <ClienteExpedienteCelda expediente={expediente} />
      {/* Linea 4: responsable (editable) + fecha con icono */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        flexWrap: 'wrap',
        fontSize: 12, color: COLOR_GRIS, marginTop: 2,
      }}>
        <ResponsableEditable
          expediente={expediente}
          usuarios={usuarios}
          onActualizar={onActualizar}
        />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: COLOR_GRIS_CL }}>
          <Calendar size={12} strokeWidth={2} />
          {fechaCorta(expediente.fecha_interes) || 's/f'}
        </span>
      </div>
      {/* CREADO-POR (2026-07-11): en tarjeta (compacto <1100) el creador va en
          su propia linea — el tooltip del folio de la tabla no existe en touch.
          Ojo: ESTA es la tarjeta viva (TarjetaFilaExpediente); la funcion
          `Tarjeta` de mas abajo es codigo muerto (hallazgo Don Porfirio). */}
      {expediente.creado_por_nombre && (
        <div style={{
          fontSize: 11.5, color: COLOR_GRIS_CL, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          Creado por {expediente.creado_por_nombre}
        </div>
      )}
      {/* Linea 5: tipo de trabajo + dias en paso (semaforo) + monto */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <TipoTrabajoChip expediente={expediente} />
          {esArchivo ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: COLOR_GRIS_CL }}>
              <Calendar size={12} strokeWidth={2} />
              Cerrado {fechaCorta(expediente.fecha_cierre || expediente.cerrado_at) || 's/f'}
            </span>
          ) : (
            <DiasSemaforoChip expediente={expediente} umbrales={umbrales} />
          )}
        </span>
        <MontoCelda expediente={expediente} />
      </div>
      {/* Pie: accion principal visible (frente 2) */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: '#fff', color: COLOR_BEEP_AZUL,
          border: `1.5px solid ${COLOR_BEEP_AZUL}55`, borderRadius: 9,
          padding: '0 14px', minHeight: 44, marginTop: 2, width: '100%',
          fontSize: 14, fontWeight: 600, fontFamily: FF, cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        Ver expediente
        <ChevronRight size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}

// ============================================
// EXP-LISTA-V2: titulo editable inline
// ============================================
function TituloEditable({ expediente, onActualizar }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(expediente.titulo || '');
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setValor(expediente.titulo || '');
  }, [expediente.titulo]);

  useEffect(() => {
    if (editando && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editando]);

  const cancelar = (e) => {
    if (e) e.stopPropagation();
    setValor(expediente.titulo || '');
    setEditando(false);
  };

  const guardar = async (e) => {
    if (e) e.stopPropagation();
    const nuevo = valor.trim();
    if (nuevo === (expediente.titulo || '')) {
      setEditando(false);
      return;
    }
    if (nuevo.length < 3) {
      // Rollback al valor anterior si quedo vacio o muy corto
      setValor(expediente.titulo || '');
      setEditando(false);
      return;
    }
    setGuardando(true);
    const ok = await onActualizar(expediente.id, { titulo: nuevo });
    setGuardando(false);
    if (ok) setEditando(false);
  };

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') guardar();
    else if (e.key === 'Escape') cancelar();
  };

  if (editando) {
    return (
      <span
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <input
          ref={inputRef}
          type="text"
          value={valor}
          onChange={(e) => setValor(e.target.value.slice(0, 150))}
          onKeyDown={onKeyDown}
          onBlur={guardar}
          maxLength={150}
          disabled={guardando}
          style={{
            border: `1px solid ${COLOR_BEEP_AZUL}`, borderRadius: 6,
            padding: '4px 8px', fontSize: 13, fontFamily: FF,
            color: COLOR_OSCURO, outline: 'none',
            minWidth: 220, background: '#fff',
          }}
        />
        {guardando && <span style={{ fontSize: 11, color: COLOR_GRIS_CL }}>guardando...</span>}
      </span>
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditando(true); }}
      title="Click para editar"
      style={{
        cursor: 'text',
        color: expediente.titulo ? COLOR_OSCURO : COLOR_GRIS_CL,
        fontWeight: expediente.titulo ? 600 : 400,
        fontStyle: expediente.titulo ? 'normal' : 'italic',
        borderRadius: 4, padding: '2px 4px',
        display: 'inline-block',
      }}
    >
      {expediente.titulo || 'Sin titulo'}
    </span>
  );
}

// ============================================
// EXP-LISTA-V2: badge estado editable inline
// ============================================
function BadgeEstadoEditable({ expediente, catEstados, onActualizar }) {
  // FLUJOS-FIJOS: el paso avanza SOLO al cumplirse el requisito (motor en
  // backend). El cambio manual queda como override exclusivo de direccion;
  // para el resto el badge es solo informativo (el backend tambien lo valida).
  const { accesoTotal } = useUsuario();
  const editable = !!accesoTotal;
  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Click fuera cierra (revisa AMBOS: trigger + dropdown porque estan en
  // arboles distintos del DOM por usar createPortal).
  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e) => {
      const enTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const enDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!enTrigger && !enDropdown) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  const abrirCerrar = (e) => {
    e.stopPropagation();
    if (abierto) { setAbierto(false); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const dropdownAlto = 320;
      const espacioAbajo = window.innerHeight - rect.bottom;
      const arribaSiNoCabe = espacioAbajo < dropdownAlto && rect.top > dropdownAlto;
      setPosicion({
        top: arribaSiNoCabe ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        transform: arribaSiNoCabe ? 'translateY(-100%)' : 'none',
      });
    }
    setAbierto(true);
  };

  const elegir = async (e, nuevoEstado) => {
    e.stopPropagation();
    if (nuevoEstado === expediente.estado) {
      setAbierto(false);
      return;
    }
    setAbierto(false);
    await onActualizar(expediente.id, { estado: nuevoEstado });
  };

  // Solo dejamos elegir entre estados activos del funnel. Lista blanca con
  // ESTADOS_VALIDOS_FUNNEL (consistente con SelectorEstadoFunnel): mantiene
  // catEstados como fuente de nombres/colores del backend, pero blinda contra
  // estados nuevos no contemplados. Los cierres (ganado/perdido/descartado)
  // se hacen desde la ficha, no desde aqui.
  const estadosActivos = (catEstados || []).filter(e =>
    ESTADOS_VALIDOS_FUNNEL.includes(e.codigo)
  );

  const color = expediente.estado_color || '#9ca3af';
  const label = expediente.estado_nombre || expediente.estado || '—';

  // Solo lectura para todos menos direccion: chip informativo, sin dropdown.
  if (!editable) {
    return (
      <span
        title="El paso avanza solo al cumplirse el requisito (ver la ficha)"
        style={{
          padding: '3px 10px', borderRadius: 99,
          fontSize: 10, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4,
          background: color + '22', color,
          fontFamily: FF, whiteSpace: 'nowrap', display: 'inline-block',
        }}
      >{label}</span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={abrirCerrar}
        title="Click para cambiar estado (override de direccion)"
        style={{
          padding: '3px 10px', borderRadius: 99, border: 'none',
          fontSize: 10, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4,
          background: color + '22', color,
          cursor: 'pointer', fontFamily: FF, whiteSpace: 'nowrap',
        }}
      >{label} {abierto ? '▴' : '▾'}</button>
      {abierto && posicion && createPortal(
        <div
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: posicion.top,
            left: posicion.left,
            transform: posicion.transform,
            zIndex: 9999,
            background: '#fff',
            border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
            boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
            minWidth: 220, padding: 4,
            maxHeight: 320, overflowY: 'auto',
          }}
        >
          {estadosActivos.length === 0 && (
            <div style={{ padding: 10, fontSize: 12, color: COLOR_GRIS_CL }}>Sin estados disponibles</div>
          )}
          {estadosActivos.map(e => {
            const sel = e.codigo === expediente.estado;
            return (
              <div
                key={e.codigo}
                onClick={(ev) => elegir(ev, e.codigo)}
                style={{
                  padding: '6px 10px', cursor: 'pointer',
                  fontSize: 12, fontFamily: FF,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: sel ? '#f3f4f6' : '#fff',
                  borderRadius: 4,
                }}
                onMouseEnter={(ev) => { if (!sel) ev.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(ev) => { if (!sel) ev.currentTarget.style.background = '#fff'; }}
              >
                <span style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: e.color || '#9ca3af', flexShrink: 0,
                }} />
                <span style={{ color: COLOR_OSCURO, flex: 1 }}>{e.nombre}</span>
                {sel && <span style={{ color: COLOR_BEEP_AZUL, fontWeight: 700 }}>{'✓'}</span>}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

// ============================================
// EXP-LISTA-V2: responsable editable inline
// ============================================
function ResponsableEditable({ expediente, usuarios, onActualizar }) {
  const [abierto, setAbierto] = useState(false);
  const [posicion, setPosicion] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const sinUsuarios = !usuarios || usuarios.length === 0;

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e) => {
      const enTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const enDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!enTrigger && !enDropdown) setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  const abrirCerrar = (e) => {
    e.stopPropagation();
    if (abierto) { setAbierto(false); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const dropdownAlto = 280;
      const espacioAbajo = window.innerHeight - rect.bottom;
      const arribaSiNoCabe = espacioAbajo < dropdownAlto && rect.top > dropdownAlto;
      setPosicion({
        top: arribaSiNoCabe ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        transform: arribaSiNoCabe ? 'translateY(-100%)' : 'none',
      });
    }
    setAbierto(true);
  };

  const elegir = async (e, nuevoId) => {
    e.stopPropagation();
    if (nuevoId === (expediente.vendedor_id || null)) {
      setAbierto(false);
      return;
    }
    setAbierto(false);
    await onActualizar(expediente.id, { vendedor_id: nuevoId });
  };

  const texto = expediente.responsable_nombre
    || <span style={{ color: COLOR_GRIS_CL, fontStyle: 'italic' }}>Sin asignar</span>;

  // Si no hay lista de usuarios, mostrar texto plano con tooltip explicativo.
  if (sinUsuarios) {
    return (
      <span title="No se pudo cargar la lista de usuarios" style={{ color: COLOR_GRIS_CL }}>
        {texto}
      </span>
    );
  }

  const usuariosVisibles = (usuarios || []).filter(u => u.activo !== false && u.estado !== 'archivado');

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={abrirCerrar}
        title="Click para reasignar"
        style={{
          background: 'transparent', border: 'none',
          padding: '2px 6px', borderRadius: 4,
          fontSize: 13, fontFamily: FF, color: COLOR_OSCURO,
          cursor: 'pointer', textAlign: 'left',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        {texto} <span style={{ color: COLOR_GRIS_CL, fontSize: 9 }}>{abierto ? '▴' : '▾'}</span>
      </button>
      {abierto && posicion && createPortal(
        <div
          ref={dropdownRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: posicion.top,
            left: posicion.left,
            transform: posicion.transform,
            zIndex: 9999,
            background: '#fff',
            border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
            boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
            minWidth: 200, maxHeight: 280, overflowY: 'auto', padding: 4,
          }}
        >
          <div
            onClick={(ev) => elegir(ev, null)}
            style={{
              padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontFamily: FF,
              color: COLOR_GRIS_CL, fontStyle: 'italic',
              borderRadius: 4,
            }}
            onMouseEnter={(ev) => { ev.currentTarget.style.background = '#f9fafb'; }}
            onMouseLeave={(ev) => { ev.currentTarget.style.background = '#fff'; }}
          >
            Sin asignar
          </div>
          {usuariosVisibles.map(u => {
            const sel = u.id === expediente.vendedor_id;
            return (
              <div
                key={u.id}
                onClick={(ev) => elegir(ev, u.id)}
                style={{
                  padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontFamily: FF,
                  color: COLOR_OSCURO, borderRadius: 4,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                  background: sel ? '#f3f4f6' : '#fff',
                }}
                onMouseEnter={(ev) => { if (!sel) ev.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(ev) => { if (!sel) ev.currentTarget.style.background = '#fff'; }}
              >
                <span>{u.nombre}</span>
                {sel && <span style={{ color: COLOR_BEEP_AZUL, fontWeight: 700 }}>{'✓'}</span>}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function ChipActivo({ label, color, onQuitar }) {
  return (
    <span
      onClick={onQuitar}
      title="Quitar filtro"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: color + '14', color, border: `1px solid ${color}55`,
        padding: '3px 8px 3px 10px', borderRadius: 99,
        fontSize: 12, fontWeight: 600, fontFamily: FF, cursor: 'pointer',
      }}
    >
      {label}
      <span style={{ fontSize: 14, lineHeight: 1, opacity: 0.7 }}>{'×'}</span>
    </span>
  );
}

// ============================================
// TARJETA DE EXPEDIENTE — lista con jerarquia visible
// ============================================
function Tarjeta({ exp, esActivos, onClick }) {
  const [hover, setHover] = useState(false);

  const estadoVisual = ESTADO_VISUAL_OPORTUNIDAD[exp.estado] || null;
  const tipoVisual   = exp.tipo_expediente && exp.tipo_expediente !== 'normal'
    ? TIPO_EXPEDIENTE_VISUAL[exp.tipo_expediente]
    : null;
  const cierreVisual = exp.tipo_cierre ? CIERRE_VISUAL[exp.tipo_cierre] : null;

  // Cliente: jerarquia primero, fallback a nombre legacy / contacto
  const tieneClienteIDs = !!(
    exp.cliente_id || exp.cliente_grupo_id || exp.cliente_razon_social_id ||
    exp.cliente_sucursal_id || exp.cliente_persona_id
  );
  const clienteTexto = exp.cliente_jerarquia_corta
    || exp.cliente_nombre
    || exp.contacto_nombre
    || (tieneClienteIDs ? 'Cliente vinculado' : 'Sin cliente');

  // Subtitulo: necesidad + tipo lugar (monto vive en cotizaciones, no aqui)
  const partesSubtitulo = [];
  if (exp.necesidad_nombre) partesSubtitulo.push(`${exp.necesidad_icono || ''} ${exp.necesidad_nombre}`.trim());
  if (exp.tipo_lugar_nombre) partesSubtitulo.push(`${exp.tipo_lugar_icono || ''} ${exp.tipo_lugar_nombre}`.trim());

  // Dias sin movimiento — solo Activos y solo si > 3
  const dias = Number(exp.dias_en_estado || 0);
  const mostrarAlertaDias = esActivos && dias > 3;

  // Iniciales para avatar del responsable
  const iniciales = (exp.responsable_nombre || '')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase()).join('') || '?';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: '#fff', borderRadius: 12, padding: 16,
        border: `1px solid ${COLOR_BORDE}`, cursor: 'pointer',
        boxShadow: hover
          ? '0 8px 20px rgba(0,0,0,0.08)'
          : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      {/* Linea 1: folio + badges */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{
            fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
            fontSize: 11, color: COLOR_GRIS_CL, fontWeight: 600,
          }}>{exp.folio}</span>

          {estadoVisual && (
            <Badge
              color={estadoVisual.color}
              label={estadoVisual.label}
              tooltip={`${estadoVisual.label}\n${estadoVisual.descripcion}`}
            />
          )}

          {tipoVisual && (
            <Badge
              color={tipoVisual.color}
              label={`${tipoVisual.icono} ${tipoVisual.label}`}
              tooltip={`${tipoVisual.label}\n${tipoVisual.descripcion}`}
            />
          )}

          {cierreVisual && (
            <Badge
              color={cierreVisual.color}
              label={cierreVisual.label}
              tooltip={`Cerrado como ${cierreVisual.label}`}
            />
          )}

          {mostrarAlertaDias && (
            <Badge
              color="#dc2626"
              label={`${dias}d sin movimiento`}
              tooltip={`Sin actualizar desde hace ${dias} dias`}
            />
          )}
        </div>

        {/* Avatar responsable a la derecha */}
        {exp.responsable_nombre && (
          <div
            title={`Responsable: ${exp.responsable_nombre}`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 11, color: COLOR_GRIS, flexShrink: 0,
            }}
          >
            <span style={{
              width: 26, height: 26, borderRadius: '50%',
              background: COLOR_BEEP_AZUL, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 700,
            }}>{iniciales}</span>
            <span style={{ fontWeight: 600, color: COLOR_OSCURO, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {exp.responsable_nombre}
            </span>
          </div>
        )}
      </div>

      {/* Linea 2: titulo (cliente) */}
      <div style={{
        color: COLOR_OSCURO, fontWeight: 700, fontSize: 15, lineHeight: 1.3,
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {clienteTexto}
      </div>

      {/* Linea 3: subtitulo */}
      {partesSubtitulo.length > 0 && (
        <div style={{ fontSize: 12, color: COLOR_GRIS, lineHeight: 1.4 }}>
          {partesSubtitulo.join('  ·  ')}
        </div>
      )}

      {/* Linea 4: vinculacion al padre */}
      {exp.expediente_padre_folio && (
        <div style={{
          fontSize: 11, color: COLOR_GRIS_CL,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        }}>
          {'↳ Vinculado a '}<span style={{ color: COLOR_BEEP_AZUL, fontWeight: 600 }}>{exp.expediente_padre_folio}</span>
        </div>
      )}

      {/* Footer: total_hijos + total_cotizaciones + creado_at */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, marginTop: 4, paddingTop: 8, borderTop: `1px solid ${COLOR_BORDE}`,
      }}>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CL }}>
          {haceCuanto(exp.creado_at)}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {Number(exp.total_hijos || 0) > 0 && (
            <span title="Tiene expedientes hijo vinculados" style={{
              background: '#fff7ed', color: '#c2410c',
              padding: '3px 8px', borderRadius: 99,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{exp.total_hijos} hijo{Number(exp.total_hijos) === 1 ? '' : 's'}</span>
          )}
          {Number(exp.total_cotizaciones || 0) > 0 && (
            <span style={{
              background: '#eef4fc', color: COLOR_BEEP_AZUL,
              padding: '3px 8px', borderRadius: 99,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>{exp.total_cotizaciones} cotizacion{Number(exp.total_cotizaciones) === 1 ? '' : 'es'}</span>
          )}
          {exp.alerta_activa && (
            <span style={{
              background: '#fee2e2', color: '#b91c1c',
              padding: '3px 8px', borderRadius: 99,
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>Atencion</span>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ color, label, tooltip }) {
  return (
    <span
      title={tooltip}
      style={{
        background: color + '22', color,
        padding: '3px 8px', borderRadius: 99,
        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
        whiteSpace: 'nowrap',
      }}
    >{label}</span>
  );
}

// ============================================
// MODAL: NUEVO EXPEDIENTE — preservado integro de B.1
// ============================================
function ModalNuevoExpediente({
  form, setForm, setCampo,
  guardando, errorForm, formularioValido,
  onCerrar, onGuardar, esCelular,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !guardando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    // MOVIL-GUARDAR: se marca TAMBIEN <html>, que es la senal que el Dashboard vigila para
    // esconder la barra inferior y el boton flotante del asistente mientras hay modal abierto.
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [guardando, onCerrar]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: esCelular ? 0 : 20, fontFamily: FF,
    }}>
      {/* Fallback dvh: los estilos inline no cascadean, asi que la altura del
          modal va por clase (vh primero como respaldo, dvh la sobrescribe donde
          se soporta). Blindaje para WebViews sin dvh (veto Don Porfirio). */}
      <style>{`
        .exp-modal-alto-movil { max-height: 92vh; max-height: 92dvh; }
        .exp-modal-alto-desktop { max-height: 90vh; max-height: 90dvh; }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        className={esCelular ? 'exp-modal-alto-movil' : 'exp-modal-alto-desktop'}
        style={{
          background: '#fff',
          borderRadius: esCelular ? '14px 14px 0 0' : 16,
          width: '100%', maxWidth: 720,
          // Frente 3: la altura (dvh con fallback vh) vive en la clase de arriba
          // para permitir la cascada; dvh descuenta las barras del navegador movil.
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          alignSelf: esCelular ? 'flex-end' : 'center',
          boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{
              margin: 0, color: COLOR_OSCURO, fontSize: 20, fontWeight: 700, fontFamily: FF,
            }}>Nuevo expediente</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              Solo lo esencial: en segundos existe y entra a su flujo. El contexto se agrega despues.
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={guardando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none', cursor: guardando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 24, fontFamily: FF,
              // touch target 44 (veto Don Porfirio)
              minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, opacity: guardando ? 0.5 : 1,
            }}
          >{'×'}</button>
        </div>

        <div style={{
          flex: 1, overflowY: 'auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 28,
        }}>
          {/* FLUJOS-FIJOS: PRIMER campo. Decide que flujo seguira el expediente. */}
          <Seccion titulo="Que tipo de trabajo es?" requerida>
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: `repeat(${esCelular ? 1 : 3}, minmax(0, 1fr))`,
            }}>
              {TIPOS_TRABAJO.map(t => {
                const sel = form.tipo_trabajo === t.id;
                const IconoTipo = t.Icono;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setCampo('tipo_trabajo', t.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '14px 14px', borderRadius: 12, minHeight: 64,
                      textAlign: 'left', fontFamily: FF, cursor: 'pointer',
                      border: sel ? `2px solid ${t.color}` : `1px solid ${COLOR_BORDE}`,
                      background: sel ? `${t.color}11` : '#fff',
                      transition: 'all 0.15s ease-out',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <IconoTipo size={22} color={t.color} style={{ flexShrink: 0 }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: sel ? t.color : COLOR_OSCURO }}>
                        {t.label}
                      </span>
                      <span style={{ display: 'block', fontSize: 12, color: COLOR_GRIS_CL, marginTop: 2 }}>
                        {t.descripcion}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </Seccion>

          {/* EXP-TITULO: titulo descriptivo del expediente (max 150, min 3) */}
          <Seccion titulo="Titulo del expediente" requerida>
            <input
              type="text"
              value={form.titulo}
              onChange={(e) => setCampo('titulo', e.target.value.slice(0, 150))}
              placeholder="Ej: Proyecto CCTV Gimnasio Brooklyn Condesa - 24 camaras"
              maxLength={150}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
              {form.titulo.length}/150 caracteres
            </div>
          </Seccion>

          {/* CATALOGO-FAMILIAS (2026-09-24): de QUE es el trabajo. No es obligatorio —
              cuando el titulo lo dice ("Cambio de camaras") el sistema lo deduce solo.
              Esta aqui para los casos en que NO lo dice, que son 12 de los expedientes
              abiertos ("Mantenimiento Septiembre" y compañia): ahi la tecnologia no vive
              en ningun campo y sin esto no hay forma de ofrecer los servicios que tocan. */}
          <Seccion titulo="Tipo de trabajo">
            <SelectorFamilia
              etiqueta={null}
              valor={form.familia}
              onChange={(v) => setForm(f => ({ ...f, familia: v || '' }))}
            />
          </Seccion>

          {/* DOS-TIEMPOS (2026-07-09): las preguntas de prospeccion (como llego,
              tipo de lugar, necesidad, prioridad/notas) se MOVIERON al Tiempo 2
              (Contexto del proyecto, inline en la vista Tareas). El alta
              queda en 4 campos: tipo de trabajo, titulo, cliente y ubicacion. */}
          <Seccion titulo="Cliente" requerida>
            <SelectorClienteExpediente
              valor={{
                cliente_id: form.cliente_id,
                cliente_grupo_id: form.cliente_grupo_id,
                cliente_razon_social_id: form.cliente_razon_social_id,
                cliente_sucursal_id: form.cliente_sucursal_id,
                cliente_persona_id: form.cliente_persona_id,
                _resumen: form._cliente_resumen,
                _detalle: form._cliente_detalle,
                _sinCliente: form._cliente_sin_vinculo,
              }}
              onChange={(nuevo) => {
                setForm(prev => ({
                  ...prev,
                  cliente_id: nuevo.cliente_id,
                  cliente_grupo_id: nuevo.cliente_grupo_id,
                  cliente_razon_social_id: nuevo.cliente_razon_social_id,
                  cliente_sucursal_id: nuevo.cliente_sucursal_id,
                  cliente_persona_id: nuevo.cliente_persona_id,
                  _cliente_resumen: nuevo._resumen,
                  _cliente_detalle: nuevo._detalle,
                  _cliente_sin_vinculo: !!nuevo._sinCliente,
                }));
              }}
              esCelular={esCelular}
            />
          </Seccion>

          {/* TAREAS-EXPEDIENTE (2026-07-10): el bloque "Sucursal o ubicacion"
              SALIO del alta — el "donde se instalara" se captura despues en el
              Contexto del proyecto (inline en la vista Tareas). Si el cliente
              tiene sucursal, se elige en el propio selector de cliente. */}

        </div>

        <div style={{
          // Frente 3: safe-area-inset-bottom para que en iPhone con home
          // indicator los botones no queden bajo la barra del sistema.
          padding: 20, paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0,
        }}>
          {errorForm && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{errorForm}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <button
              onClick={onCerrar}
              disabled={guardando}
              style={{
                background: '#fff', color: COLOR_GRIS, border: `1px solid ${COLOR_BORDE}`,
                borderRadius: 10, padding: '11px 20px', fontSize: 14,
                fontWeight: 600, fontFamily: FF, cursor: guardando ? 'not-allowed' : 'pointer',
                minHeight: 44, opacity: guardando ? 0.5 : 1,
              }}
            >Cancelar</button>
            <button
              onClick={onGuardar}
              disabled={!formularioValido || guardando}
              style={{
                background: (!formularioValido || guardando) ? '#cbd5e1' : COLOR_BEEP_LIMA,
                color: '#fff', border: 'none',
                borderRadius: 10, padding: '11px 22px', fontSize: 14,
                fontWeight: 700, fontFamily: FF,
                cursor: (!formularioValido || guardando) ? 'not-allowed' : 'pointer',
                minHeight: 44, transition: 'background 0.2s',
              }}
            >{guardando ? 'Creando...' : 'Crear expediente'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPERS DE UI (preservados de la version previa)
// ============================================
const inputStyle = {
  width: '100%', padding: '11px 12px',
  border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
  // EXP-UX frente 3: 16px evita el AUTO-ZOOM de iOS al enfocar un input, que
  // descuadraba el modal en el telefono (el footer quedaba fuera de vista).
  fontSize: 16, fontFamily: FF, color: COLOR_OSCURO,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

function Seccion({ titulo, requerida, children }) {
  return (
    <div>
      <h3 style={{
        margin: '0 0 14px', color: COLOR_OSCURO, fontSize: 14, fontWeight: 700, fontFamily: FF,
      }}>{titulo}{requerida && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}</h3>
      {children}
    </div>
  );
}

function Campo({ etiqueta, requerido, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontSize: 12, color: COLOR_GRIS, fontWeight: 600, fontFamily: FF,
      }}>{etiqueta}{requerido && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}</span>
      {children}
    </label>
  );
}

